// HelpView.js (Admin Panel)
// Tabs: Device Setup, Backup Guide, Stream Setup (overlay config + alerts),
// Designs. Accessible from the main menu. Staff-only by nav context.

import React, { useState, useEffect, useRef } from "react";
import { db, storage } from "./firebase";
import { resolveShift } from "./utils/shiftScope";
import { collection, doc, addDoc, getDocs, deleteDoc, onSnapshot, orderBy, query, where, limit, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { STAFF_THEME, FONT } from "./theme/staff";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { describeSaveError } from "./utils/authDebug";
import { seedTestBooking, seedTestOrders, seedTestPreUploads, createPreviewToken, countTestData, cleanTestData, seedCoupons } from "./utils/testData";
// Stream Setup promoted to its own top-level route (/stream-setup).
// PromoTickets moved into RegisterView as a modal (since moved again → /campaigns).

const PRINTER_IP = "192.168.1.69";
const BRAND = STAFF_THEME;

// ── SETUP STEPS ───────────────────────────────────────────────────────────────
const SETUP_STEPS = [
  {
    id: "network",
    title: "Enable local network access",
    icon: "📡",
    instructions: [
      "Open the iPhone Settings app",
      "Scroll down and tap Safari",
      "Scroll down and tap Local Network",
      "Make sure the toggle is ON",
    ],
    note: "This allows Safari to communicate with the printer on the booth WiFi.",
    action: null,
    secondaryAction: null,
  },
  {
    id: "cert",
    title: "Install the printer certificate",
    icon: "🔐",
    instructions: [
      "Make sure your device is connected to the Nighthawk hotspot",
      'Tap "Download certificate" below and follow the prompts',
      "If the download stalls, use the manual method instead (see note below)",
      "Open the Files app and tap the downloaded .cer or .crt file",
      "Go to Settings → General → VPN & Device Management",
      "Tap the certificate and tap Install",
      "Go to Settings → General → About → Certificate Trust Settings",
      "Find the certificate and toggle Enable Full Trust ON",
    ],
    note: "If the download button stalls: open the printer web config below, log in with the printer serial number (printed on the label on the bottom of the printer), go to Network Security → SSL/TLS → Certificate, and tap Download from there.",
    action: {
      label: "Download certificate",
      url: `http://${PRINTER_IP}/cgi-bin/dynamic/printer/cfg_security.crt`,
    },
    secondaryAction: {
      label: "Open printer web config (manual fallback)",
      url: `http://${PRINTER_IP}`,
    },
  },
  {
    id: "homescreen",
    title: "Add app to home screen",
    icon: "📱",
    instructions: [
      "Open this app in Safari (not Brave or Chrome)",
      "Tap the Share button — the box with an arrow pointing up",
      "Scroll down in the share sheet",
      'Tap "Add to Home Screen"',
      "Name it Golden Ticket and tap Add",
    ],
    note: "The app must be opened from the home screen icon for the best experience. Brave and Chrome do not support all features.",
    action: null,
    secondaryAction: null,
  },
  {
    id: "signin",
    title: "Sign in",
    icon: "🔑",
    instructions: [
      "Open the Golden Ticket app from your home screen",
      "Tap Sign in with Google",
      "Use your Joy of Pancakes Google account",
    ],
    note: "Only authorized accounts can access the register view.",
    action: null,
    secondaryAction: null,
  },
  {
    id: "test",
    title: "Test print",
    icon: "🖨️",
    instructions: [
      "Make sure the Nighthawk hotspot is on",
      "Make sure the Epson printer is on and connected",
      "Navigate to the Register view",
      "Tap Flash — a test ticket should print within a few seconds",
    ],
    note: "If printing fails, confirm the printer IP is 192.168.1.69 and the hotspot is active.",
    action: {
      label: "Open printer web config",
      url: `http://${PRINTER_IP}`,
    },
    secondaryAction: null,
  },
];

// ── BACKUP STEPS ──────────────────────────────────────────────────────────────
const BACKUP_STEPS = [
  {
    id: "prereqs",
    title: "Prerequisites (one-time setup)",
    icon: "🔧",
    body: "You've already done this. Just confirming what was installed.",
    items: [
      { label: "Homebrew", detail: "Mac package manager — installed at /opt/homebrew" },
      { label: "Google Cloud SDK", detail: "Installed via Homebrew. Provides gsutil for photo sync." },
      { label: "gcloud auth login", detail: "Authenticated as jump@thejoyofpancakes.com. Credentials last ~60 days — re-run this command if the backup commands start failing." },
      { label: "gcloud auth application-default login", detail: "Separate credential used by the Firestore export script. Also lasts ~60 days." },
    ],
    note: "If either auth command needs to be re-run, open Terminal and run:\n/opt/homebrew/share/google-cloud-sdk/bin/gcloud auth login\n/opt/homebrew/share/google-cloud-sdk/bin/gcloud auth application-default login",
    code: null,
  },
  {
    id: "code",
    title: "Back up the source code",
    icon: "💾",
    body: "Copies the full app source to your Extreme SSD. Only transfers files that have changed, so it's fast after the first run.",
    items: null,
    note: "The build/ and node_modules/ folders are excluded — they can be regenerated with npm install && npm run build.",
    code: `rsync -av --exclude='node_modules' --exclude='build' ~/golden-ticket/ "/Volumes/Extreme SSD/golden-ticket-backup/golden-ticket/"`,
  },
  {
    id: "firestore",
    title: "Back up Firestore data",
    icon: "🗄️",
    body: "Exports all orders, shifts, tokens, summaries, and personal bests to a dated JSON folder on the SSD.",
    items: null,
    note: "Creates a new folder each run named firestore-export-YYYY-MM-DD. Old exports are kept — you can delete them manually to save space.",
    code: `cd ~/golden-ticket/functions && GOOGLE_CLOUD_PROJECT=the-golden-ticket-system node export-firestore.js`,
  },
  {
    id: "photos",
    title: "Back up photos",
    icon: "📸",
    body: "Syncs all pancake photos and QR codes from Firebase Storage to the SSD. Only downloads new or changed files — runs in seconds once you've done it once.",
    items: null,
    note: "Photos are the one truly irreplaceable asset. Run this after every event.",
    code: `/opt/homebrew/share/google-cloud-sdk/bin/gsutil -m rsync -r gs://the-golden-ticket-system.firebasestorage.app/ "/Volumes/Extreme SSD/golden-ticket-backup/storage-latest/"`,
  },
  {
    id: "rebuild",
    title: "How to rebuild from backup",
    icon: "🔄",
    body: "If you ever need to restore the app from your SSD backup:",
    items: [
      { label: "Copy the code folder", detail: 'Copy golden-ticket/ from the SSD back to your Mac home directory' },
      { label: "Install dependencies", detail: "Open Terminal, cd ~/golden-ticket, run: npm install" },
      { label: "Restore Firebase config", detail: "The firebase.json and src/firebase.js are included in the backup — they contain your project connection details" },
      { label: "Deploy", detail: "Run: npm run build && firebase deploy --only hosting" },
      { label: "Photos", detail: "Re-upload from storage-latest/ using gsutil rsync in reverse, or they may still be in Firebase Storage if the project is intact" },
    ],
    note: "The Firebase project itself (Firestore, Storage, Auth) lives in Google's cloud and is separate from this backup. This backup protects your code and a snapshot of your data.",
    code: null,
  },
];

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function HelpView({ navigate }) {
  const [tab, setTab] = useState("setup");

  return (
    <div style={{ minHeight: "100vh", background: BRAND.dark, fontFamily: FONT.body, color: "white", display: "flex", flexDirection: "column" }}>
      {/* Header — ← Menu pinned right. */}
      <div style={{ padding: "0.75rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, display: "flex", alignItems: "center", gap: "1rem", flexShrink: 0 }}>
        <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1rem", flex: 1 }}>🛠 Admin</div>
        <button onClick={() => navigate("/menu")} style={hudBtn}>← Menu</button>
      </div>

      {/* Tab switcher — restructured 2026-05-28. "Dev Tools" → split into
          Test Harness (fixtures, safe), Maintenance (cost-sensitive
          production-data mutations), and Data (backup + sync). Old
          Designs + Flash Sets tabs merged. */}
      <div style={{ display: "flex", background: BRAND.panel, borderBottom: `1px solid ${BRAND.divider}`, flexShrink: 0 }}>
        {[
          { id: "setup",       label: "🛠 Setup" },
          { id: "designs",     label: "🎨 Designs" },
          { id: "data",        label: "💾 Data" },
          { id: "maintenance", label: "🧰 Maintenance" },
          { id: "test",        label: "🧪 Test Harness" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1,
            padding: "0.75rem",
            background: "transparent",
            border: "none",
            borderBottom: tab === t.id ? `2px solid ${BRAND.gold}` : "2px solid transparent",
            color: tab === t.id ? BRAND.gold : BRAND.dim,
            fontWeight: tab === t.id ? "bold" : "normal",
            fontSize: "0.82rem",
            cursor: "pointer",
          }}>{t.label}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {tab === "setup"       && <SetupTab />}
        {tab === "designs"     && <><DesignsTab /><FlashSetsTab /></>}
        {tab === "data"        && <DataTab />}
        {tab === "maintenance" && <MaintenanceTab />}
        {tab === "test"        && <DevToolsTab navigate={navigate} />}
      </div>
    </div>
  );
}

// ── SETUP TAB ─────────────────────────────────────────────────────────────────
function SetupTab() {
  const [active, setActive] = useState(0);
  const step    = SETUP_STEPS[active];
  const isFirst = active === 0;
  const isLast  = active === SETUP_STEPS.length - 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Progress dots */}
      <div style={{ display: "flex", justifyContent: "center", gap: "8px", padding: "1rem" }}>
        {SETUP_STEPS.map((s, i) => (
          <button key={s.id} onClick={() => setActive(i)} style={{
            width: "10px", height: "10px", borderRadius: "50%", border: "none", padding: 0, cursor: "pointer",
            background: i === active ? BRAND.gold : i < active ? BRAND.sky : BRAND.raised,
          }} />
        ))}
      </div>

      {/* Step content */}
      <div style={{ flex: 1, padding: "1rem 1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem", overflowY: "auto" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>{step.icon}</div>
          <div style={{ color: BRAND.dim, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.25rem" }}>
            Step {active + 1} of {SETUP_STEPS.length}
          </div>
          <h2 style={{ margin: 0, fontSize: "1.3rem", color: BRAND.gold }}>{step.title}</h2>
        </div>

        <div style={{ background: BRAND.card, borderRadius: "12px", padding: "1.25rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {step.instructions.map((instruction, i) => (
            <div key={i} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
              <span style={{ background: BRAND.gold, color: BRAND.dark, borderRadius: "50%", width: "22px", height: "22px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: "bold", flexShrink: 0, marginTop: "1px" }}>
                {i + 1}
              </span>
              <span style={{ fontSize: "0.95rem", lineHeight: 1.5, color: "rgba(255,255,255,0.85)" }}>{instruction}</span>
            </div>
          ))}
        </div>

        <div style={{ background: "rgba(74,164,225,0.1)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: "8px", padding: "0.75rem 1rem", fontSize: "0.85rem", color: BRAND.sky, lineHeight: 1.5 }}>
          ℹ️ {step.note}
        </div>

        {step.action && (
          <a href={step.action.url} target="_blank" rel="noreferrer" style={{ background: BRAND.raised, color: "white", border: `1px solid ${BRAND.border}`, borderRadius: "10px", padding: "0.85rem", fontSize: "0.95rem", textAlign: "center", textDecoration: "none", display: "block" }}>
            🔗 {step.action.label}
          </a>
        )}
        {step.secondaryAction && (
          <a href={step.secondaryAction.url} target="_blank" rel="noreferrer" style={{ background: BRAND.card, color: BRAND.muted, border: `1px solid ${BRAND.border}`, borderRadius: "10px", padding: "0.85rem", fontSize: "0.9rem", textAlign: "center", textDecoration: "none", display: "block" }}>
            🔧 {step.secondaryAction.label}
          </a>
        )}
      </div>

      {/* Navigation */}
      <div style={{ display: "flex", gap: "1rem", padding: "1rem 1.5rem", borderTop: `1px solid ${BRAND.divider}` }}>
        <button onClick={() => setActive(active - 1)} disabled={isFirst} style={{ flex: 1, background: isFirst ? BRAND.panel : BRAND.card, color: isFirst ? BRAND.dim : "white", border: "none", borderRadius: "10px", padding: "0.85rem", fontSize: "1rem", cursor: isFirst ? "not-allowed" : "pointer" }}>
          ← Previous
        </button>
        <button onClick={() => setActive(active + 1)} disabled={isLast} style={{ flex: 2, background: isLast ? BRAND.raised : BRAND.gold, color: isLast ? BRAND.dim : BRAND.dark, border: "none", borderRadius: "10px", padding: "0.85rem", fontSize: "1rem", fontWeight: "bold", cursor: isLast ? "default" : "pointer" }}>
          {isLast ? "✓ All done" : "Next →"}
        </button>
      </div>
    </div>
  );
}

// ── BACKUP TAB ────────────────────────────────────────────────────────────────
function BackupTab({ inline = false }) {
  const [copied, setCopied] = useState(null);

  function copyCode(id, text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={{ padding: inline ? 0 : "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div style={{ color: BRAND.muted, fontSize: "0.88rem", lineHeight: 1.6 }}>
        Run these three commands in Terminal after each event. Plug in the Extreme SSD first.
      </div>

      {BACKUP_STEPS.map(step => (
        <div key={step.id} style={{ background: BRAND.card, borderRadius: "12px", padding: "1.1rem", display: "flex", flexDirection: "column", gap: "0.7rem", border: `1px solid ${BRAND.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <span style={{ fontSize: "1.4rem" }}>{step.icon}</span>
            <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.95rem" }}>{step.title}</div>
          </div>

          <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.85rem", lineHeight: 1.5 }}>{step.body}</div>

          {step.items && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {step.items.map((item, i) => (
                <div key={i} style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
                  <span style={{ color: BRAND.pink, fontWeight: "bold", fontSize: "0.8rem", flexShrink: 0, marginTop: "2px" }}>•</span>
                  <div>
                    <span style={{ color: "white", fontSize: "0.85rem", fontWeight: "600" }}>{item.label}</span>
                    <span style={{ color: BRAND.muted, fontSize: "0.82rem" }}> — {item.detail}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {step.code && (
            <div style={{ position: "relative" }}>
              <div style={{ background: "#060c20", borderRadius: "8px", padding: "0.75rem 2.5rem 0.75rem 0.85rem", fontFamily: "monospace", fontSize: "0.75rem", color: "#b8f0b8", lineHeight: 1.6, wordBreak: "break-all", border: "1px solid rgba(74,164,225,0.2)" }}>
                {step.code}
              </div>
              <button
                onClick={() => copyCode(step.id, step.code)}
                style={{ position: "absolute", top: "0.5rem", right: "0.5rem", background: copied === step.id ? BRAND.green : BRAND.raised, border: "none", borderRadius: "6px", color: "white", fontSize: "0.7rem", padding: "0.25rem 0.5rem", cursor: "pointer" }}
              >
                {copied === step.id ? "✓" : "Copy"}
              </button>
            </div>
          )}

          <div style={{ background: "rgba(74,164,225,0.08)", border: "1px solid rgba(74,164,225,0.25)", borderRadius: "6px", padding: "0.6rem 0.75rem", fontSize: "0.78rem", color: BRAND.sky, lineHeight: 1.5, whiteSpace: "pre-line" }}>
            ℹ️ {step.note}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── DESIGNS TAB ──────────────────────────────────────────────────────────────
const HARDCODED_DESIGNS = [
  { id: "charli",         label: "Charli",           imageUrl: "/flash-designs/Charli.png",         active: true, quote: "Obviously.",                                                        subtitle: "the Superstar" },
  { id: "hermiethewurmy", label: "Hermiethewurmy",   imageUrl: "/flash-designs/Hermiethewurmy.png", active: true, quote: "Oh, I don't need much.",                                             subtitle: "Humble Farmer" },
  { id: "robert",         label: "Trav",             imageUrl: "/flash-designs/Robert.png",         active: true, quote: "✦ ·͜· ✦ ˖° ...What high school did you go to? I love the Blues.",   subtitle: "the Tourist" },
  { id: "saturnina",      label: "Saturnina",        imageUrl: "/flash-designs/Saturnina.png",      active: true, quote: "Right on schedule, dear.",                                            subtitle: "Galactic Nanny" },
  { id: "sunnySugar",     label: "SunnynSugar",      imageUrl: "/flash-designs/SunnySugar.png",     active: true, quote: "We're SO excited!!|...She's excited.",                                 subtitle: "the Sister Act" },
  { id: "freestyle",      label: "Freestyle",        imageUrl: "/flash-designs/Freestyle.png",      active: true, isFreestyle: true, quote: "Mistakes are delicious!" },
];

function DesignsTab() {
  const [designs, setDesigns] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [uploading, setUploading] = useState(false);
  // Tracks which row (if any) the file picker should replace instead of
  // appending. null = add new design; number = replace imageUrl on that row.
  const [replacingIdx, setReplacingIdx] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "config", "flashDesigns"), snap => {
      if (snap.exists() && Array.isArray(snap.data().designs)) {
        setDesigns(snap.data().designs);
      } else {
        setDesigns(HARDCODED_DESIGNS);
      }
    }, () => setDesigns(HARDCODED_DESIGNS));
    return unsub;
  }, []);

  async function save() {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "flashDesigns"), { designs }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { alert("Save failed: " + describeSaveError(err)); }
    setSaving(false);
  }

  function updateDesign(idx, field, value) {
    setDesigns(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  }
  function removeDesign(idx) {
    setDesigns(prev => prev.filter((_, i) => i !== idx));
  }
  function moveDesign(idx, dir) {
    const target = idx + dir;
    if (target < 0 || target >= designs.length) return;
    setDesigns(prev => {
      const arr = [...prev];
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  }

  // Single file-picker that serves both actions. If `replacingIdx` is set,
  // the upload swaps the imageUrl on that row only (keeps label/quote/
  // subtitle/position/active). Otherwise it appends a new design. A fresh
  // timestamped storage path is used either way so cached thumbnails don't
  // stick after a swap.
  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) { setReplacingIdx(null); return; }
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["png", "svg", "jpg", "jpeg"].includes(ext)) {
      alert("Supported formats: PNG, SVG, JPG");
      setReplacingIdx(null);
      return;
    }
    setUploading(true);
    try {
      const cleanName = file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_-]/g, "_").toLowerCase();
      const path = `flash-designs/${cleanName}_${Date.now()}.${ext}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file, { contentType: file.type });
      const imageUrl = await getDownloadURL(ref);

      if (replacingIdx != null) {
        // Swap only the artwork — everything else about the design stays put.
        setDesigns(prev => prev.map((d, i) => i === replacingIdx ? { ...d, imageUrl } : d));
      } else {
        const label = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
        const id    = cleanName + "_" + Date.now();
        setDesigns(prev => [...prev, { id, label, imageUrl, active: true, quote: "" }]);
      }
    } catch (err) { alert("Upload failed: " + describeSaveError(err)); }
    setUploading(false);
    setReplacingIdx(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  // Opens the single hidden file picker, pre-targeted at a specific row so
  // handleUpload knows to swap instead of append.
  function triggerReplace(idx) {
    setReplacingIdx(idx);
    // Defer so React commits the state change before the picker reads it.
    setTimeout(() => fileRef.current?.click(), 0);
  }

  if (!designs) return <div style={{ color: BRAND.muted, fontSize: "0.85rem", padding: "1rem", textAlign: "center" }}>Loading designs…</div>;

  return (
    <div style={{ padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div style={{ background: BRAND.card, borderRadius: "12px", padding: "1.1rem", display: "flex", flexDirection: "column", gap: "0.75rem", border: `1px solid ${BRAND.border}` }}>
        <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.95rem" }}>🎨 Flash Designs</div>
        <div style={{ color: BRAND.muted, fontSize: "0.78rem", lineHeight: 1.5 }}>
          Manage the flash design menu. Upload PNG or SVG files. Toggle designs on/off, reorder, rename.
          Changes go live when you tap Save.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {designs.map((d, i) => (
            <div key={d.id} style={{
              background: BRAND.raised, borderRadius: "8px", padding: "0.6rem",
              display: "flex", flexDirection: "column", gap: "0.4rem",
              border: `1px solid ${d.active ? "rgba(74,164,225,0.3)" : "rgba(255,255,255,0.08)"}`,
              opacity: d.active ? 1 : 0.5,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <input
                  type="checkbox" checked={d.active}
                  onChange={e => updateDesign(i, "active", e.target.checked)}
                  style={{ width: "18px", height: "18px", flexShrink: 0, cursor: "pointer", accentColor: BRAND.sky }}
                />
                <div style={{
                  width: "48px", height: "48px", borderRadius: "6px", flexShrink: 0, overflow: "hidden",
                  background: "#000", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <img src={d.imageUrl} alt={d.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
                <input
                  type="text" value={d.label}
                  onChange={e => updateDesign(i, "label", e.target.value)}
                  style={{ flex: 1, background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "white", fontSize: "0.82rem", padding: "0.35rem 0.5rem", outline: "none", minWidth: 0 }}
                />
                {d.isFreestyle && <span style={{ color: BRAND.sky, fontSize: "0.65rem", flexShrink: 0 }}>freestyle</span>}
                <div style={{ display: "flex", gap: "0.25rem", flexShrink: 0 }}>
                  <button
                    onClick={() => triggerReplace(i)}
                    disabled={uploading}
                    title="Replace artwork (keep name, quote, subtitle, position)"
                    style={{ ...miniBtn, color: BRAND.sky, borderColor: "rgba(74,164,225,0.35)" }}
                  >
                    🔄
                  </button>
                  <button onClick={() => moveDesign(i, -1)} disabled={i === 0} style={{ ...miniBtn, opacity: i === 0 ? 0.3 : 1 }}>↑</button>
                  <button onClick={() => moveDesign(i, 1)} disabled={i === designs.length - 1} style={{ ...miniBtn, opacity: i === designs.length - 1 ? 0.3 : 1 }}>↓</button>
                  <button onClick={() => removeDesign(i)} style={{ ...miniBtn, color: "#ef537e" }}>✕</button>
                </div>
              </div>
              <div style={{ marginLeft: "26px", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                {!d.isFreestyle && (
                  <input
                    type="text" value={d.subtitle || ""}
                    onChange={e => updateDesign(i, "subtitle", e.target.value)}
                    placeholder="Subtitle (e.g. the Superstar)"
                    style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", padding: "0.3rem 0.5rem", outline: "none" }}
                  />
                )}
                <input
                  type="text" value={d.quote || ""}
                  onChange={e => updateDesign(i, "quote", e.target.value)}
                  placeholder={d.isFreestyle ? "Speech bubble quote…" : "Character quote…"}
                  style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "rgba(255,255,255,0.6)", fontSize: "0.75rem", fontStyle: "italic", padding: "0.3rem 0.5rem", outline: "none" }}
                />
                {!d.isFreestyle && (
                  <textarea
                    value={d.blurb || ""}
                    onChange={e => updateDesign(i, "blurb", e.target.value)}
                    placeholder="Character bio — who they are, vibe, visual signature. Fed to AI alt-text when this design is drawn. Leave blank to use the built-in bible."
                    rows={3}
                    style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", padding: "0.35rem 0.5rem", outline: "none", resize: "vertical", fontFamily: "inherit", lineHeight: 1.4 }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: "0.5rem" }}>
          <input ref={fileRef} type="file" accept=".png,.svg,.jpg,.jpeg" onChange={handleUpload} style={{ display: "none" }} />
          <button
            onClick={() => { setReplacingIdx(null); fileRef.current?.click(); }}
            disabled={uploading}
            style={{ flex: 1, background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.sky, fontSize: "0.82rem", padding: "0.55rem", cursor: "pointer", fontWeight: "600" }}
          >
            {uploading ? (replacingIdx != null ? "Replacing…" : "Uploading…") : "+ Upload Design (PNG / SVG)"}
          </button>
          <button onClick={save} disabled={saving} style={{ flex: 1, background: saved ? BRAND.green : BRAND.gold, color: saved ? "white" : BRAND.dark, border: "none", borderRadius: "8px", fontSize: "0.82rem", padding: "0.55rem", cursor: "pointer", fontWeight: "bold" }}>
            {saving ? "Saving…" : saved ? "✓ Saved!" : "Save changes"}
          </button>
        </div>
      </div>

      <div style={{ background: "rgba(74,164,225,0.08)", border: "1px solid rgba(74,164,225,0.25)", borderRadius: "8px", padding: "0.75rem 1rem", fontSize: "0.78rem", color: BRAND.sky, lineHeight: 1.6, display: "flex", flexDirection: "column", gap: "0.3rem" }}>
        <div style={{ fontWeight: "bold" }}>ℹ️ Design Guidelines</div>
        <div>• <strong>PNG files</strong>: Transparent background, any size. Good for logos, illustrations, existing art.</div>
        <div>• <strong>SVG files</strong>: Use separate layers for outlines and fill zones. Name zones semantically (e.g. body, hat, cheeks) for future color customization.</div>
        <div>• Square or near-square aspect ratios display best in the picker grid.</div>
        <div>• The "Freestyle" design should stay last — it's the catch-all for text-only requests.</div>
      </div>

      <ColorPaletteEditor />
    </div>
  );
}

// ── COLOR PALETTE EDITOR ──────────────────────────────────────────────────
// Reads/writes to config/colorPalette in Firestore. Falls back to the
// hardcoded COLOR_OPTIONS from flashDesigns.js. Changes here update what
// guests see on the order form color picker.
function ColorPaletteEditor() {
  const DEFAULT_COLORS = [
    { id: 'red',    label: 'Red',    hex: '#E84040' },
    { id: 'orange', label: 'Orange', hex: '#F5821F' },
    { id: 'yellow', label: 'Yellow', hex: '#FFD426' },
    { id: 'green',  label: 'Green',  hex: '#3DC45E' },
    { id: 'blue',   label: 'Blue',   hex: '#1f59db' },
    { id: 'indigo', label: 'Indigo', hex: '#3a3d9e' },
    { id: 'violet', label: 'Violet', hex: '#9B3DBF' },
    { id: 'pink',   label: 'Pink',   hex: '#F06FA8' },
    { id: 'black',  label: 'Black',  hex: '#1E1E1E' },
    { id: 'white',  label: 'White',  hex: '#F0F0F0' },
  ];

  const [colors, setColors] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "config", "colorPalette"), snap => {
      if (snap.exists() && Array.isArray(snap.data().colors)) {
        setColors(snap.data().colors);
      } else {
        setColors(DEFAULT_COLORS);
      }
    }, () => setColors(DEFAULT_COLORS));
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "colorPalette"), { colors });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { alert("Save failed: " + describeSaveError(err)); }
    setSaving(false);
  }

  function resetToDefaults() {
    setColors(DEFAULT_COLORS);
  }

  if (!colors) return null;

  return (
    <div style={{ borderTop: `1px solid ${BRAND.border}`, paddingTop: "1.25rem", marginTop: "1.5rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
        <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.95rem" }}>🎨 Guest Favorite Colors</div>
        <div style={{ display: "flex", gap: "0.4rem" }}>
          <button onClick={resetToDefaults} style={{ background: "transparent", border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.muted, fontSize: "0.75rem", padding: "0.3rem 0.6rem", cursor: "pointer" }}>Reset</button>
          <button onClick={save} disabled={saving} style={{ background: BRAND.gold, color: BRAND.dark, border: "none", borderRadius: "8px", fontWeight: "bold", fontSize: "0.8rem", padding: "0.4rem 1rem", cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saving…" : saved ? "✓ Saved" : "Save"}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "0.5rem" }}>
        {colors.map((c, i) => (
          <div key={c.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.3rem" }}>
            <label style={{ position: "relative", cursor: "pointer" }}>
              <input
                type="color"
                value={c.hex}
                onChange={e => setColors(prev => prev.map((cc, j) => j === i ? { ...cc, hex: e.target.value } : cc))}
                style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
              />
              <div style={{
                width: "42px", height: "42px", borderRadius: "10px",
                background: c.hex,
                border: c.id === "white" ? "2px solid rgba(255,255,255,0.3)" : c.id === "black" ? "2px solid rgba(255,255,255,0.2)" : "2px solid transparent",
                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                cursor: "pointer",
              }} />
            </label>
            <div style={{ color: BRAND.muted, fontSize: "0.65rem", textAlign: "center" }}>{c.label}</div>
            <div style={{ color: BRAND.dim, fontSize: "0.55rem", fontFamily: "monospace" }}>{c.hex}</div>
          </div>
        ))}
      </div>

      <div style={{ color: BRAND.dim, fontSize: "0.7rem", marginTop: "0.5rem" }}>
        Tap a swatch to open the color picker. Changes apply to the guest order form after saving.
      </div>
    </div>
  );
}

// ── FLASH SETS TAB ────────────────────────────────────────────────────────
// Manages flashDesignProfiles — curated bundles of flash designs that can be
// activated per-shift (in addition to or instead of the base IP set). Each
// profile has its own designs[] in the same shape as config/flashDesigns so
// the merge in useFlashDesigns is trivial. Profiles can be tagged with a
// CRM booking id (so opening that shift auto-loads them) or kept generic.
function FlashSetsTab() {
  const [profiles, setProfiles] = useState(null);
  const [view, setView]         = useState({ kind: "list" }); // { kind: "list" } | { kind: "edit", id }

  useEffect(() => {
    const q = query(collection(db, "flashDesignProfiles"), orderBy("name"));
    return onSnapshot(q, snap => {
      setProfiles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => setProfiles([]));
  }, []);

  async function createProfile() {
    const ref = await addDoc(collection(db, "flashDesignProfiles"), {
      name: "Untitled set",
      clientName: null,
      isClientSet: false,
      designs: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    setView({ kind: "edit", id: ref.id });
  }

  async function removeProfile(id, name) {
    if (!window.confirm(`Delete "${name}"? Shifts referencing it will fall back to the base set.`)) return;
    await deleteDoc(doc(db, "flashDesignProfiles", id));
  }

  if (!profiles) {
    return <div style={{ color: BRAND.muted, fontSize: "0.85rem", padding: "1rem", textAlign: "center" }}>Loading sets…</div>;
  }

  if (view.kind === "edit") {
    return <FlashSetEditor profileId={view.id} onBack={() => setView({ kind: "list" })} />;
  }

  return (
    <div style={{ padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div style={{ background: BRAND.card, borderRadius: "12px", padding: "1.1rem", display: "flex", flexDirection: "column", gap: "0.75rem", border: `1px solid ${BRAND.border}` }}>
        <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.95rem" }}>📚 Flash Design Sets</div>
        <div style={{ color: BRAND.muted, fontSize: "0.78rem", lineHeight: 1.5 }}>
          Curated bundles of flash designs for specific events or clients (e.g. Father's Day @ Brookfield).
          Activated per-shift from the open-shift screen. Set a default on a CRM booking to auto-load.
        </div>

        {profiles.length === 0 && (
          <div style={{ color: BRAND.dim, fontSize: "0.82rem", fontStyle: "italic", padding: "0.5rem 0" }}>
            No sets yet. Create one to get started.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {profiles.map(p => (
            <div key={p.id} style={{
              background: BRAND.raised, borderRadius: "8px", padding: "0.7rem 0.85rem",
              display: "flex", alignItems: "center", gap: "0.75rem",
              border: `1px solid ${p.isClientSet ? "rgba(245,118,0,0.35)" : "rgba(255,255,255,0.08)"}`,
            }}>
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                <div style={{ color: "white", fontSize: "0.95rem", fontWeight: "600", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.name || "Untitled set"}
                </div>
                <div style={{ color: BRAND.muted, fontSize: "0.72rem" }}>
                  {(p.designs || []).length} design{(p.designs || []).length === 1 ? "" : "s"}
                  {p.clientName ? ` · ${p.clientName}` : ""}
                  {p.isClientSet ? " · client set" : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: "0.4rem", flexShrink: 0 }}>
                <button onClick={() => setView({ kind: "edit", id: p.id })}
                  style={{ ...miniBtn, color: BRAND.sky, borderColor: "rgba(74,164,225,0.35)", padding: "0.35rem 0.6rem" }}>
                  Edit
                </button>
                <button onClick={() => removeProfile(p.id, p.name)}
                  style={{ ...miniBtn, color: "#ef537e", padding: "0.35rem 0.6rem" }}>
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>

        <button onClick={createProfile}
          style={{ background: BRAND.gold, color: BRAND.dark, border: "none", borderRadius: "8px", fontSize: "0.85rem", padding: "0.6rem", cursor: "pointer", fontWeight: "bold" }}>
          + Create new set
        </button>
      </div>
    </div>
  );
}

// Editor for a single flash design profile. Mirrors DesignsTab's per-row
// affordances (upload / rename / toggle / reorder / replace artwork) but
// scoped to one profile doc. Saves the whole designs[] array on Save.
function FlashSetEditor({ profileId, onBack }) {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [replacingIdx, setReplacingIdx] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    return onSnapshot(doc(db, "flashDesignProfiles", profileId), snap => {
      if (!snap.exists()) { setProfile(null); return; }
      setProfile({ id: snap.id, ...snap.data() });
    });
  }, [profileId]);

  function update(field, value) {
    setProfile(prev => ({ ...prev, [field]: value }));
  }
  function updateDesign(idx, field, value) {
    setProfile(prev => ({ ...prev, designs: prev.designs.map((d, i) => i === idx ? { ...d, [field]: value } : d) }));
  }
  function removeDesign(idx) {
    setProfile(prev => ({ ...prev, designs: prev.designs.filter((_, i) => i !== idx) }));
  }
  function moveDesign(idx, dir) {
    const target = idx + dir;
    setProfile(prev => {
      if (target < 0 || target >= prev.designs.length) return prev;
      const arr = [...prev.designs];
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return { ...prev, designs: arr };
    });
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) { setReplacingIdx(null); return; }
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["png", "svg", "jpg", "jpeg"].includes(ext)) {
      alert("Supported formats: PNG, SVG, JPG");
      setReplacingIdx(null);
      return;
    }
    setUploading(true);
    try {
      const cleanName = file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_-]/g, "_").toLowerCase();
      const path = `flash-designs/${profileId}_${cleanName}_${Date.now()}.${ext}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file, { contentType: file.type });
      const imageUrl = await getDownloadURL(ref);

      if (replacingIdx != null) {
        setProfile(prev => ({ ...prev, designs: prev.designs.map((d, i) => i === replacingIdx ? { ...d, imageUrl } : d) }));
      } else {
        const label = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
        const id    = cleanName + "_" + Date.now();
        setProfile(prev => ({ ...prev, designs: [...(prev.designs || []), { id, label, imageUrl, active: true, quote: "" }] }));
      }
    } catch (err) { alert("Upload failed: " + describeSaveError(err)); }
    setUploading(false);
    setReplacingIdx(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  function triggerReplace(idx) {
    setReplacingIdx(idx);
    setTimeout(() => fileRef.current?.click(), 0);
  }

  async function save() {
    setSaving(true);
    try {
      await updateDoc(doc(db, "flashDesignProfiles", profileId), {
        name: profile.name || "Untitled set",
        clientName: profile.clientName || null,
        isClientSet: !!profile.isClientSet,
        designs: profile.designs || [],
        updatedAt: serverTimestamp(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { alert("Save failed: " + describeSaveError(err)); }
    setSaving(false);
  }

  if (!profile) {
    return (
      <div style={{ padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        <button onClick={onBack} style={{ ...miniBtn, alignSelf: "flex-start", padding: "0.4rem 0.7rem" }}>← Back to sets</button>
        <div style={{ color: BRAND.muted, fontSize: "0.85rem", padding: "1rem", textAlign: "center" }}>Loading…</div>
      </div>
    );
  }

  const designs = profile.designs || [];

  return (
    <div style={{ padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
      <button onClick={onBack} style={{ ...miniBtn, alignSelf: "flex-start", padding: "0.4rem 0.7rem" }}>← Back to sets</button>

      <div style={{ background: BRAND.card, borderRadius: "12px", padding: "1.1rem", display: "flex", flexDirection: "column", gap: "0.75rem", border: `1px solid ${BRAND.border}` }}>
        <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.95rem" }}>📚 Edit Flash Set</div>

        <input
          type="text" value={profile.name || ""}
          onChange={e => update("name", e.target.value)}
          placeholder="Set name (e.g. Father's Day @ Brookfield)"
          style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "white", fontSize: "0.95rem", padding: "0.55rem 0.7rem", outline: "none", fontWeight: "600" }}
        />
        <input
          type="text" value={profile.clientName || ""}
          onChange={e => update("clientName", e.target.value)}
          placeholder="Client name (optional)"
          style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "rgba(255,255,255,0.85)", fontSize: "0.82rem", padding: "0.45rem 0.6rem", outline: "none" }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: "0.55rem", color: BRAND.muted, fontSize: "0.78rem", cursor: "pointer" }}>
          <input type="checkbox" checked={!!profile.isClientSet} onChange={e => update("isClientSet", e.target.checked)} style={{ accentColor: BRAND.sky }} />
          Mark as client-supplied set (for filtering / org)
        </label>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginTop: "0.4rem" }}>
          {designs.length === 0 && (
            <div style={{ color: BRAND.dim, fontSize: "0.8rem", fontStyle: "italic", padding: "0.5rem 0" }}>
              No designs yet. Upload PNGs or SVGs below.
            </div>
          )}
          {designs.map((d, i) => (
            <div key={d.id} style={{
              background: BRAND.raised, borderRadius: "8px", padding: "0.6rem",
              display: "flex", flexDirection: "column", gap: "0.4rem",
              border: `1px solid ${d.active ? "rgba(74,164,225,0.3)" : "rgba(255,255,255,0.08)"}`,
              opacity: d.active ? 1 : 0.5,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <input type="checkbox" checked={d.active}
                  onChange={e => updateDesign(i, "active", e.target.checked)}
                  style={{ width: "18px", height: "18px", flexShrink: 0, cursor: "pointer", accentColor: BRAND.sky }} />
                <div style={{ width: "48px", height: "48px", borderRadius: "6px", flexShrink: 0, overflow: "hidden", background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img src={d.imageUrl} alt={d.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
                <input type="text" value={d.label}
                  onChange={e => updateDesign(i, "label", e.target.value)}
                  style={{ flex: 1, background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "white", fontSize: "0.82rem", padding: "0.35rem 0.5rem", outline: "none", minWidth: 0 }} />
                <div style={{ display: "flex", gap: "0.25rem", flexShrink: 0 }}>
                  <button onClick={() => triggerReplace(i)} disabled={uploading}
                    title="Replace artwork"
                    style={{ ...miniBtn, color: BRAND.sky, borderColor: "rgba(74,164,225,0.35)" }}>
                    🔄
                  </button>
                  <button onClick={() => moveDesign(i, -1)} disabled={i === 0} style={{ ...miniBtn, opacity: i === 0 ? 0.3 : 1 }}>↑</button>
                  <button onClick={() => moveDesign(i, 1)} disabled={i === designs.length - 1} style={{ ...miniBtn, opacity: i === designs.length - 1 ? 0.3 : 1 }}>↓</button>
                  <button onClick={() => removeDesign(i)} style={{ ...miniBtn, color: "#ef537e" }}>✕</button>
                </div>
              </div>
              <div style={{ marginLeft: "26px", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                <input type="text" value={d.subtitle || ""}
                  onChange={e => updateDesign(i, "subtitle", e.target.value)}
                  placeholder="Subtitle (optional)"
                  style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", padding: "0.3rem 0.5rem", outline: "none" }} />
                <input type="text" value={d.quote || ""}
                  onChange={e => updateDesign(i, "quote", e.target.value)}
                  placeholder="Character quote (optional)"
                  style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "rgba(255,255,255,0.6)", fontSize: "0.75rem", fontStyle: "italic", padding: "0.3rem 0.5rem", outline: "none" }} />
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: "0.5rem" }}>
          <input ref={fileRef} type="file" accept=".png,.svg,.jpg,.jpeg" onChange={handleUpload} style={{ display: "none" }} />
          <button onClick={() => { setReplacingIdx(null); fileRef.current?.click(); }} disabled={uploading}
            style={{ flex: 1, background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.sky, fontSize: "0.82rem", padding: "0.55rem", cursor: "pointer", fontWeight: "600" }}>
            {uploading ? (replacingIdx != null ? "Replacing…" : "Uploading…") : "+ Add design"}
          </button>
          <button onClick={save} disabled={saving}
            style={{ flex: 1, background: saved ? BRAND.green : BRAND.gold, color: saved ? "white" : BRAND.dark, border: "none", borderRadius: "8px", fontSize: "0.82rem", padding: "0.55rem", cursor: "pointer", fontWeight: "bold" }}>
            {saving ? "Saving…" : saved ? "✓ Saved!" : "Save set"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── DEV TOOLS TAB ─────────────────────────────────────────────────────────
// Seed + cleanup harness for feature testing. Right now it's scoped to the
// booking ↔ shift linkage (one-tap test booking for today, so the
// ShiftManager pre-fill suggestion + CRM "View shift →" + Library deep-link
// round-trip can be exercised in seconds instead of the 5-minute CRM form-
// fill ritual). Future linkages can stack seeders in here as they land.
// localStorage keys for the persisted chip state. Keep these stable —
// renaming would orphan saved deep-links from prior sessions.
const LS_LAST_RESULT   = "gt_devtools_lastResult";
const LS_PREVIEW_LINKS = "gt_devtools_previewLinks";

function loadJsonLs(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}

function DevToolsTab({ navigate }) {
  const [counts,     setCounts]     = useState(null);
  const [busy,       setBusy]       = useState(null);   // "seed" | "clean" | "orders" | "preview_*" | null
  // lastResult + previewLinks persist across page navigation so the
  // deep-link chips from a fresh seed/preview don't vanish when staff
  // bounces to /queue or /crm to verify what was created.
  const [lastResult, setLastResult] = useState(() => loadJsonLs(LS_LAST_RESULT, null));
  // Track full shift doc (not just id) so we can read _isTestData to decide
  // whether to confirm before seeding fake orders into a real shift.
  const [activeShift, setActiveShift] = useState(null);
  const [previewLinks, setPreviewLinks] = useState(() => loadJsonLs(LS_PREVIEW_LINKS, {}));
  useEffect(() => {
    try { localStorage.setItem(LS_LAST_RESULT, JSON.stringify(lastResult)); } catch { /* quota / private mode */ }
  }, [lastResult]);
  useEffect(() => {
    try { localStorage.setItem(LS_PREVIEW_LINKS, JSON.stringify(previewLinks)); } catch { /* quota / private mode */ }
  }, [previewLinks]);

  async function refresh() {
    try { setCounts(await countTestData()); } catch (err) { console.warn(err); }
  }

  useEffect(() => { refresh(); }, []);

  // Listen for active shift so we know whether "seed orders" is available
  useEffect(() => {
    const q = query(collection(db, "shifts"), where("isActive", "==", true));
    const unsub = onSnapshot(q, (snap) => {
      // Scoped so Dev Tools seeds into THIS device's queue, not the other
      // venue's live one.
      setActiveShift(resolveShift(snap.docs.map(d => ({ id: d.id, ...d.data() }))).shift);
    });
    return () => unsub();
  }, []);

  async function onSeedOrders() {
    // Real-shift guardrail — fake orders are visible to real viewers in the
    // queue + OBS overlays + /events live card during the seconds before
    // purge. If the active shift isn't flagged as test data, ask first.
    if (activeShift && !activeShift._isTestData) {
      const ok = window.confirm(
        `⚠️ "${activeShift.eventName || "This shift"}" is a REAL shift. Fake test orders will briefly appear in the queue, on OBS overlays, and on the public /events live-shift card before you purge them. Continue?`
      );
      if (!ok) return;
    }
    setBusy("orders");
    setLastResult(null);
    try {
      const created = await seedTestOrders(activeShift?.id, 4);
      setLastResult({ kind: "orders", created, when: Date.now() });
      await refresh();
    } catch (err) {
      setLastResult({ kind: "error", msg: err?.message || "Order seeding failed", when: Date.now() });
    } finally {
      setBusy(null);
    }
  }

  async function onSeedPreUploads() {
    // Pre-uploads stay in the staging tray (staff-only) until promoted, so
    // the blast radius is smaller than seed orders — but still warn if the
    // booking they bind to is a real one. seedTestPreUploads resolves the
    // target booking from the active shift, so we check the same way.
    if (activeShift && !activeShift._isTestData) {
      const ok = window.confirm(
        `⚠️ "${activeShift.eventName || "This shift"}" is linked to a REAL booking. The fake pre-uploads will appear in the CRM "Pre-uploaded designs" panel and the staging tray until you purge them. Continue?`
      );
      if (!ok) return;
    }
    setBusy("preuploads");
    setLastResult(null);
    try {
      const result = await seedTestPreUploads();
      setLastResult({ kind: "preuploads", ...result, when: Date.now() });
      await refresh();
    } catch (err) {
      setLastResult({ kind: "error", msg: err?.message || "Pre-upload seeding failed", when: Date.now() });
    } finally {
      setBusy(null);
    }
  }

  async function onPreviewToken(type) {
    const key = `preview_${type}`;
    setBusy(key);
    try {
      const { url } = await createPreviewToken(type);
      setPreviewLinks(prev => ({ ...prev, [type]: url }));
      await refresh();
    } catch (err) {
      setLastResult({ kind: "error", msg: err?.message || "Token creation failed", when: Date.now() });
    } finally {
      setBusy(null);
    }
  }

  async function onSeed() {
    setBusy("seed");
    setLastResult(null);
    try {
      const { id } = await seedTestBooking();
      setLastResult({ kind: "seed", id, when: Date.now() });
      await refresh();
    } catch (err) {
      setLastResult({ kind: "error", msg: err?.message || "Seed failed", when: Date.now() });
    } finally {
      setBusy(null);
    }
  }

  // 🤖 AI-generated test booking — calls Claude Haiku via Cloud Function to
  // invent a fully-fleshed-out fake booking (random client name, company,
  // dates, services, totals). Used for UI-density testing of the CRM
  // detail/list views. Moved from the /crm primary toolbar into Test
  // Harness so the CRM stops carrying a dev affordance.
  async function onGenerateAiBooking() {
    setBusy("aiBooking");
    setLastResult(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "bookingGenerateTestData");
      const res = await fn({});
      setLastResult({ kind: "aiBooking", id: res.data.bookingId, when: Date.now() });
      await refresh();
    } catch (err) {
      setLastResult({ kind: "error", msg: err?.message || "AI booking generation failed", when: Date.now() });
    } finally {
      setBusy(null);
    }
  }

  async function onClean() {
    if (!window.confirm("Purge every doc flagged _isTestData across bookings, shifts, shift_summaries, orders? This can't be undone, but those docs should only exist from the seed button above.")) return;
    setBusy("clean");
    setLastResult(null);
    try {
      const removed = await cleanTestData();
      setLastResult({ kind: "clean", removed, when: Date.now() });
      await refresh();
    } catch (err) {
      setLastResult({ kind: "error", msg: err?.message || "Cleanup failed", when: Date.now() });
    } finally {
      setBusy(null);
    }
  }

  const totalTestDocs = counts ? (counts.bookings + counts.shifts + counts.shiftSummaries + counts.orders + (counts.tokens || 0)) : 0;

  return (
    <div style={{ padding: "1.25rem 1.25rem 2rem", display: "flex", flexDirection: "column", gap: "1.1rem" }}>

      {/* Intro */}
      <div style={{ color: BRAND.muted, fontSize: "0.88rem", lineHeight: 1.55 }}>
        Synthetic fixtures for feature testing. Every doc written here is flagged{" "}
        <code style={{ background: BRAND.card, padding: "0.05rem 0.3rem", borderRadius: "4px", fontSize: "0.82rem" }}>_isTestData</code>{" "}
        and filtered from live CRM lists + shift archive. One-tap cleanup sweeps everything flagged.
      </div>

      {/* Counts */}
      <div style={{
        background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "10px",
        padding: "0.85rem 1rem", display: "flex", alignItems: "center", gap: "1rem",
      }}>
        <div style={{
          fontSize: "1.8rem", fontWeight: 800,
          color: totalTestDocs > 0 ? BRAND.gold : BRAND.dim,
          minWidth: "2.5rem", textAlign: "center",
        }}>{counts ? totalTestDocs : "…"}</div>
        <div style={{ flex: 1, fontSize: "0.82rem", lineHeight: 1.5, color: BRAND.muted }}>
          {counts
            ? <>
                Test docs in database — {counts.bookings} booking{counts.bookings !== 1 ? "s" : ""},{" "}
                {counts.shifts} shift{counts.shifts !== 1 ? "s" : ""}
                {counts.shiftSummaries > 0 && `, ${counts.shiftSummaries} summar${counts.shiftSummaries !== 1 ? "ies" : "y"}`}
                {counts.orders > 0 && `, ${counts.orders} order${counts.orders !== 1 ? "s" : ""}`}
                {(counts.tokens || 0) > 0 && `, ${counts.tokens} token${counts.tokens !== 1 ? "s" : ""}`}
              </>
            : "Loading test-data counts…"}
        </div>
        <button onClick={refresh} style={{ ...miniBtn, padding: "0.3rem 0.6rem", fontSize: "0.78rem" }}>↻</button>
      </div>

      {/* Seed section */}
      <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🧪 Seed today's test booking</div>
        <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
          Creates a public booking scheduled for today with stand-in client info.
          Opens the door for ShiftManager's pre-fill suggestion, the CRM{" "}
          <em>View shift →</em> chip, and{" "}
          <code style={{ background: BRAND.card, padding: "0.05rem 0.25rem", borderRadius: "4px", fontSize: "0.78rem" }}>/library?tab=shifts&amp;shiftId=…</code>{" "}
          deep-links to exercise end-to-end.
        </div>
        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
          <button
            onClick={onSeed}
            disabled={busy !== null}
            style={{
              background: busy === "seed" ? BRAND.panel : BRAND.blue, color: "white", border: "none",
              borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
              cursor: busy ? "default" : "pointer", opacity: busy && busy !== "seed" ? 0.5 : 1,
            }}
          >
            {busy === "seed" ? "Seeding…" : "🧪 Seed test booking"}
          </button>
          {lastResult?.kind === "seed" && (
            <>
              <button
                onClick={() => navigate(`/crm/bookings/${lastResult.id}`)}
                style={{ ...miniBtn, padding: "0.55rem 0.9rem", fontSize: "0.82rem", color: BRAND.sky, borderColor: BRAND.sky }}
              >
                View in CRM →
              </button>
              <button
                onClick={() => navigate("/queue")}
                style={{ ...miniBtn, padding: "0.55rem 0.9rem", fontSize: "0.82rem", color: BRAND.green, borderColor: BRAND.green }}
              >
                Go to Artist Queue →
              </button>
            </>
          )}
        </div>
      </section>

      {/* AI-generated test booking — Claude Haiku invents a fully-fleshed-
          out fake booking with random client/dates/services. Used for UI-
          density testing of the CRM list + detail views. Was previously on
          the /crm primary toolbar; moved here so the CRM stays clean. */}
      <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🤖 Generate AI test booking</div>
        <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
          Calls Claude Haiku (~$0.001/call) to invent a fully-filled fake booking — random client name, company, dates, services, totals.
          Use to test CRM UI density. Slower than the stub booking above; richer for visual testing.
        </div>
        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
          <button
            onClick={onGenerateAiBooking}
            disabled={busy !== null}
            style={{
              background: busy === "aiBooking" ? BRAND.panel : BRAND.pink, color: "white", border: "none",
              borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
              cursor: busy ? "default" : "pointer", opacity: busy && busy !== "aiBooking" ? 0.5 : 1,
            }}
          >
            {busy === "aiBooking" ? "Generating (5-10s)…" : "🤖 Generate AI booking"}
          </button>
          {lastResult?.kind === "aiBooking" && (
            <button
              onClick={() => navigate(`/crm/bookings/${lastResult.id}`)}
              style={{ ...miniBtn, padding: "0.55rem 0.9rem", fontSize: "0.82rem", color: BRAND.sky, borderColor: BRAND.sky }}
            >
              View in CRM →
            </button>
          )}
        </div>
      </section>

      {/* Seed test orders */}
      <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🎫 Seed test orders</div>
        <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
          Injects 4 fake queued orders into the active shift — mix of custom (with/without notes) and flash.
          Tests queue display, name callout, notes modal, QR reissue, and image state.
          {!activeShift?.id && <span style={{ color: BRAND.orange, fontWeight: 600 }}> Open a shift first.</span>}
        </div>
        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
          <button
            onClick={onSeedOrders}
            disabled={busy !== null || !activeShift?.id}
            style={{
              background: !activeShift?.id ? BRAND.panel : (busy === "orders" ? BRAND.panel : BRAND.orange),
              color: !activeShift?.id ? BRAND.dim : "white", border: "none",
              borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
              cursor: (busy || !activeShift?.id) ? "default" : "pointer",
              opacity: busy && busy !== "orders" ? 0.5 : 1,
            }}
          >
            {busy === "orders" ? "Seeding\u2026" : !activeShift?.id ? "No active shift" : "🎫 Seed 4 test orders"}
          </button>
          {lastResult?.kind === "orders" && (
            <button
              onClick={() => navigate("/queue")}
              style={{ ...miniBtn, padding: "0.55rem 0.9rem", fontSize: "0.82rem", color: BRAND.green, borderColor: BRAND.green }}
            >
              Go to Artist Queue →
            </button>
          )}
        </div>
        {lastResult?.kind === "orders" && (
          <div style={{ background: "rgba(74,164,225,0.08)", border: `1px solid ${BRAND.sky}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
            ✓ Seeded {lastResult.created.length} orders: {lastResult.created.map(o => `#${o.queueNumber} ${o.name}`).join(" · ")}
          </div>
        )}
      </section>

      {/* Seed test pre-uploads — exercises the staging tray + promotion flow
          before the client upload form (step 3) ships. Auto-discovers the
          target booking from the active shift, or any test booking. */}
      <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>📥 Seed test pre-uploads</div>
        <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
          Stages 3 fake client pre-uploads against the active shift's booking (or any test booking if no shift is open).
          Tests the staging tray on /queue + the CRM "Pre-uploaded designs" panel + the promote-to-queue flow.
        </div>
        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
          <button
            onClick={onSeedPreUploads}
            disabled={busy !== null}
            style={{
              background: busy === "preuploads" ? BRAND.panel : BRAND.gold,
              color: busy === "preuploads" ? BRAND.dim : BRAND.dark, border: "none",
              borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
              cursor: busy ? "default" : "pointer",
              opacity: busy && busy !== "preuploads" ? 0.5 : 1,
            }}
          >
            {busy === "preuploads" ? "Seeding…" : "📥 Seed 3 test pre-uploads"}
          </button>
          {lastResult?.kind === "preuploads" && (
            <>
              <button
                onClick={() => navigate("/queue")}
                style={{ ...miniBtn, padding: "0.55rem 0.9rem", fontSize: "0.82rem", color: BRAND.green, borderColor: BRAND.green }}
              >
                Go to Artist Queue →
              </button>
              <button
                onClick={() => navigate(`/crm/bookings/${lastResult.bookingId}`)}
                style={{ ...miniBtn, padding: "0.55rem 0.9rem", fontSize: "0.82rem", color: BRAND.sky, borderColor: BRAND.sky }}
              >
                View booking in CRM →
              </button>
              <a
                href={`/upload?t=${lastResult.tokenId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...miniBtn, padding: "0.55rem 0.9rem", fontSize: "0.82rem", color: BRAND.gold, borderColor: BRAND.gold, textDecoration: "none" }}
              >
                Open /upload page ↗
              </a>
            </>
          )}
        </div>
        {lastResult?.kind === "preuploads" && (
          <div style={{ background: "rgba(252,179,31,0.08)", border: `1px solid ${BRAND.gold}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
            ✓ Staged {lastResult.created.length} pre-uploads against booking <code style={{ color: BRAND.gold }}>{lastResult.bookingId.slice(0, 8)}…</code>: {lastResult.created.map(o => o.name).join(" · ")}
          </div>
        )}
      </section>

      {/* Preview order flow */}
      <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>📱 Preview guest order flow</div>
        <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
          Generate a fresh ticket link for any type. Opens the real guest ordering experience — tap the link or scan from your phone.
          {!activeShift?.id && <span style={{ color: BRAND.orange, fontWeight: 600 }}> Needs an active shift to accept orders.</span>}
        </div>
        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
          {[
            { type: "custom", label: "🎨 Custom", color: BRAND.blue },
            { type: "flash",  label: "⚡ Flash",  color: BRAND.orange },
            { type: "magic",  label: "✨ Magic",  color: BRAND.pink },
          ].map(({ type, label, color }) => (
            <button
              key={type}
              onClick={() => onPreviewToken(type)}
              disabled={busy !== null}
              style={{
                background: busy === `preview_${type}` ? BRAND.panel : color,
                color: "white", border: "none", borderRadius: "10px",
                padding: "0.6rem 1rem", fontSize: "0.88rem", fontWeight: 700,
                cursor: busy ? "default" : "pointer",
                opacity: busy && busy !== `preview_${type}` ? 0.5 : 1,
              }}
            >
              {busy === `preview_${type}` ? "Creating\u2026" : label}
            </button>
          ))}
        </div>
        {Object.entries(previewLinks).map(([type, url]) => (
          <div key={type} style={{
            background: "rgba(74,164,225,0.08)", border: `1px solid ${BRAND.border}`,
            borderRadius: "10px", padding: "0.7rem 1rem",
            display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
          }}>
            <span style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.85rem", textTransform: "capitalize" }}>{type}</span>
            <a href={url} target="_blank" rel="noopener noreferrer" style={{
              flex: 1, color: BRAND.sky, fontSize: "0.8rem", wordBreak: "break-all", textDecoration: "underline",
            }}>{url}</a>
            <button onClick={() => { navigator.clipboard.writeText(url); }} style={{
              ...miniBtn, padding: "0.35rem 0.6rem", fontSize: "0.75rem",
            }}>Copy</button>
          </div>
        ))}
      </section>

      {/* Seed coupons — one-tap setup for coupon codes */}
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <button
          onClick={async () => { setBusy("coupons"); try { await seedCoupons(); setLastResult({ kind: "coupons", when: Date.now() }); } catch (e) { setLastResult({ kind: "error", msg: e.message, when: Date.now() }); } finally { setBusy(null); } }}
          disabled={busy !== null}
          style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.muted, fontSize: "0.78rem", padding: "0.4rem 0.8rem", cursor: "pointer" }}
        >
          {busy === "coupons" ? "..." : "🎫 Seed coupon codes"}
        </button>
        {lastResult?.kind === "coupons" && <span style={{ color: BRAND.green, fontSize: "0.78rem" }}>✓ DANTEST + PANCAKE10 seeded</span>}
      </div>

      {/* Cleanup section */}
      <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🧹 Clean up test data</div>
        <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
          Purges every doc flagged <code style={{ background: BRAND.card, padding: "0.05rem 0.25rem", borderRadius: "4px", fontSize: "0.78rem" }}>_isTestData: true</code> from
          bookings, shifts, shift_summaries, and orders. Real booth data is never touched.
        </div>
        <div>
          <button
            onClick={onClean}
            disabled={busy !== null || totalTestDocs === 0}
            style={{
              background: totalTestDocs === 0 ? BRAND.panel : (busy === "clean" ? BRAND.panel : "#a03030"),
              color: totalTestDocs === 0 ? BRAND.dim : "white",
              border: "none", borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
              cursor: (busy || totalTestDocs === 0) ? "default" : "pointer",
              opacity: busy && busy !== "clean" ? 0.5 : 1,
            }}
          >
            {busy === "clean" ? "Cleaning…" : totalTestDocs === 0 ? "Nothing to clean" : `🧹 Purge ${totalTestDocs} test doc${totalTestDocs !== 1 ? "s" : ""}`}
          </button>
        </div>
      </section>

      {/* Last-result readout */}
      {lastResult?.kind === "clean" && (
        <div style={{ background: "rgba(112,190,68,0.08)", border: `1px solid ${BRAND.green}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ✓ Cleaned: {lastResult.removed.bookings} bookings · {lastResult.removed.shifts} shifts · {lastResult.removed.shift_summaries} summaries · {lastResult.removed.orders} orders
        </div>
      )}
      {lastResult?.kind === "seed" && (
        <div style={{ background: "rgba(74,164,225,0.08)", border: `1px solid ${BRAND.sky}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ✓ Seeded booking <code style={{ fontSize: "0.78rem" }}>{lastResult.id}</code>. ShiftManager should now surface it as today's public event.
        </div>
      )}
      {lastResult?.kind === "error" && (
        <div style={{ background: "rgba(201,68,68,0.12)", border: "1px solid #c94444", borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ⚠ {lastResult.msg}
        </div>
      )}

    </div>
  );
}

// ── DATA TAB ─────────────────────────────────────────────────────────────────
// Operations that move data between this app and external systems / backups.
// Backup commands stay here (offline-grade rsync + gsutil); calendar import +
// rebuild public docs + backfill series live inside CalendarImportSection.
function DataTab() {
  return (
    <div style={{ padding: "1.25rem 1.25rem 2rem", display: "flex", flexDirection: "column", gap: "1.1rem" }}>
      <div style={{ color: BRAND.muted, fontSize: "0.88rem", lineHeight: 1.55 }}>
        Backups + calendar import. Backup commands run from your laptop; the import writes
        bookings to Firestore. (Looking for the public-site refresh? It moved to
        Maintenance → Public site.)
      </div>
      <BackupTab inline />
      <CalendarImportSection />
    </div>
  );
}

// ── MAINTENANCE TAB ──────────────────────────────────────────────────────────
// Production-data repairs + audits + integration checks, bundled into titled
// groups so the tab reads as a toolbox, not a pile. Each tool keeps its own
// confirm dialog because each permanently mutates real data (some cost real
// money — AI description backfill calls Anthropic at ~$0.011/pancake).

// Visual bundle for a set of related maintenance tools: panel card with a
// header + one-line purpose, children separated by soft dividers. Pass
// `collapsible` for rarely-needed groups — they render closed with just the
// header until tapped.
function MaintGroup({ icon, title, blurb, collapsible = false, children }) {
  const [open, setOpen] = useState(!collapsible);
  const kids = React.Children.toArray(children);
  return (
    <div style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "14px", overflow: "hidden" }}>
      <div
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
        style={{ padding: "0.85rem 1rem", display: "flex", alignItems: "baseline", gap: "0.55rem", cursor: collapsible ? "pointer" : "default", userSelect: "none" }}
      >
        <span style={{ fontSize: "1rem" }}>{icon}</span>
        <span style={{ color: "white", fontWeight: 700, fontSize: "1rem" }}>{title}</span>
        <span style={{ color: BRAND.dim, fontSize: "0.8rem", flex: 1 }}>{blurb}</span>
        {collapsible && (
          <span style={{ color: BRAND.dim, fontSize: "0.8rem" }}>{open ? "▾ hide" : "▸ show"}</span>
        )}
      </div>
      {open && kids.map((child, i) => (
        <div key={i} style={{ padding: "0.9rem 1rem 1.1rem", borderTop: `1px solid rgba(74,164,225,0.14)` }}>
          {child}
        </div>
      ))}
    </div>
  );
}

function MaintenanceTab() {
  return (
    <div style={{ padding: "1.25rem 1.25rem 2rem", display: "flex", flexDirection: "column", gap: "1.1rem" }}>
      <div style={{ color: BRAND.muted, fontSize: "0.88rem", lineHeight: 1.55 }}>
        The toolbox for fixing and checking production data. Start at the top: repairs fix
        real orders, the public-site refresh pushes changes to the website, audits are
        read-only, and the bottom groups are rarely needed.
      </div>

      <MaintGroup icon="🔧" title="Repairs" blurb="fix production order data — each asks before writing">
        <BackfillDesignSequencesSection />
        <BackfillHistoricalShiftsSection />
        <BackfillAiDescriptionsSection />
      </MaintGroup>

      <MaintGroup icon="🌐" title="Public site" blurb="push fresh data to the public pages">
        <RefreshPublicDataSection />
      </MaintGroup>

      <MaintGroup icon="🔍" title="Audits" blurb="read-only checks — safe to run anytime">
        <ShiftSummaryAudit />
      </MaintGroup>

      <MaintGroup icon="🔌" title="Integration checks" blurb="prove a connected service still works end-to-end">
        <BlueskyConnectionTestSection />
        <PrintfulConnectionTestSection />
      </MaintGroup>

      <MaintGroup icon="🗃" title="Launch & one-time tools" blurb="POD setup gates + past experiments — safe to ignore" collapsible>
        <CreatePodStickerSheetSection />
        <PrintfulStickerDiscoverySection />
        <PrintfulMockupTestSection />
        <CutoutAbComparisonSection />
      </MaintGroup>
    </div>
  );
}

// ── Create POD sticker sheet in Square Catalog ─────────────────────────────
// One-shot: creates the "Print on Demand" Square category (if missing) +
// "Pancake Sticker Sheet" item at $15, then triggers an immediate catalog
// sync. After this runs, the item appears in /shop with a "Coming soon"
// badge and `isPOD: true` flows through the pipeline.
function CreatePodStickerSheetSection() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    if (!window.confirm("Create 'Pancake Sticker Sheet' at $15 + 'Print on Demand' category in Square Catalog? Idempotent — safe to re-run.")) return;
    setBusy(true); setResult(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "createPodStickerSheetItem", { timeout: 90000 });
      const res = await fn({});
      setResult(res.data);
    } catch (err) {
      setResult({ error: err?.message || "Catalog creation failed" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🏷️ Create POD sticker sheet in Square</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Creates the "Print on Demand" Square category (if missing) and the "Pancake Sticker Sheet"
        item at $15 via the Square Catalog API, then triggers an immediate <code style={{ background: BRAND.card, padding: "0.05rem 0.25rem", borderRadius: "4px", fontSize: "0.78rem" }}>syncShopCatalog</code>.
        Idempotent — re-running won't duplicate.
      </div>
      <div>
        <button onClick={run} disabled={busy} style={{
          background: busy ? BRAND.panel : BRAND.orange, color: "white", border: "none",
          borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
          cursor: busy ? "default" : "pointer",
        }}>
          {busy ? "Creating…" : "🏷️ Create item"}
        </button>
      </div>
      {result && !result.error && (
        <div style={{ background: "rgba(112,190,68,0.08)", border: `1px solid ${BRAND.green}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
          <div>✓ Created and synced.</div>
          <div style={{ fontFamily: "ui-monospace, monospace", fontSize: "0.78rem", color: BRAND.cream }}>
            item_id: {result.itemId}<br/>
            variation_id: {result.variationId}<br/>
            category_id: {result.categoryId}<br/>
            price: ${(result.priceCents / 100).toFixed(2)}
          </div>
          <div style={{ marginTop: "0.3rem", color: BRAND.dim, fontSize: "0.78rem" }}>
            Check <a href="/shop" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.gold }}>/shop</a> — should appear with "Coming soon" badge.
          </div>
        </div>
      )}
      {result?.error && (
        <div style={{ background: "rgba(201,68,68,0.12)", border: "1px solid #c94444", borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ⚠ {result.error}
        </div>
      )}
    </section>
  );
}

// ── Printful sticker discovery ──────────────────────────────────────────────
// Lists all sticker-ish products in Printful's catalog with their variants
// + IDs. Used once to identify which variant ID we want for our launch SKU.
function PrintfulStickerDiscoverySection() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    setBusy(true); setResult(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "discoverPrintfulStickers", { timeout: 120000 });
      const res = await fn({});
      setResult(res.data);
    } catch (err) {
      setResult({ error: err?.message || "Discovery failed" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🖨️ Discover Printful sticker products</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Lists all sticker products in Printful's catalog with their variants (sizes + IDs).
        Use this once to find the right variant ID for the kiss-cut 5×5" we want to launch with.
      </div>
      <div>
        <button onClick={run} disabled={busy} style={{
          background: busy ? BRAND.panel : BRAND.blue, color: "white", border: "none",
          borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
          cursor: busy ? "default" : "pointer",
        }}>
          {busy ? "Fetching…" : "🖨️ Discover sticker products"}
        </button>
      </div>
      {result && !result.error && (
        <div style={{ background: BRAND.card, border: `1px solid ${BRAND.panel}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.82rem", color: "white", maxHeight: "400px", overflowY: "auto" }}>
          <div style={{ marginBottom: "0.5rem", color: BRAND.gold }}>{result.count} sticker product(s):</div>
          {result.products.map((p) => (
            <div key={p.id} style={{ marginBottom: "1rem", paddingBottom: "0.8rem", borderBottom: `1px solid ${BRAND.panel}` }}>
              <div style={{ fontWeight: 700 }}>{p.title} <span style={{ opacity: 0.55, fontWeight: 400 }}>(product_id: {p.id})</span></div>
              {p.description && <div style={{ color: BRAND.dim, fontSize: "0.76rem", marginTop: "0.2rem" }}>{p.description}</div>}
              <div style={{ marginTop: "0.4rem", fontFamily: "ui-monospace, monospace", fontSize: "0.75rem", color: BRAND.cream }}>
                {(p.variants || []).map((v) => (
                  <div key={v.id}>
                    variant_id <strong style={{ color: BRAND.gold }}>{v.id}</strong>
                    {v.size && ` · ${v.size}`} · ${v.price}
                    {!v.in_stock && <span style={{ color: "#c94444" }}> (out of stock)</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {result?.error && (
        <div style={{ background: "rgba(201,68,68,0.12)", border: "1px solid #c94444", borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ⚠ {result.error}
        </div>
      )}
    </section>
  );
}

// ── Printful mockup test ────────────────────────────────────────────────────
// Renders a chosen pancake onto a chosen Printful product variant. Lets us
// eyeball the mockup quality before wiring it into the customer flow.
function PrintfulMockupTestSection() {
  const [orderId, setOrderId] = useState("");
  const [productId, setProductId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    if (!orderId.trim() || !productId.trim() || !variantId.trim()) {
      setResult({ error: "All three fields required." });
      return;
    }
    setBusy(true); setResult(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "testPrintfulMockup", { timeout: 180000 });
      const res = await fn({
        orderId:   orderId.trim(),
        productId: productId.trim(),
        variantId: variantId.trim(),
      });
      setResult(res.data);
    } catch (err) {
      setResult({ error: err?.message || "Mockup generation failed" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🖨️ Test Printful mockup generation</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Generates a mockup of a pancake on a Printful product. Use the order ID of any pancake with a cutout,
        plus the product_id + variant_id from the discovery step above. Takes 5–15 seconds.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
        <input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="pancake order ID"
          style={{ background: BRAND.card, color: "white", border: `1px solid ${BRAND.panel}`, borderRadius: "8px", padding: "0.55rem 0.7rem", fontSize: "0.82rem", fontFamily: "ui-monospace, monospace" }} />
        <input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="product_id"
          style={{ background: BRAND.card, color: "white", border: `1px solid ${BRAND.panel}`, borderRadius: "8px", padding: "0.55rem 0.7rem", fontSize: "0.82rem", fontFamily: "ui-monospace, monospace" }} />
        <input value={variantId} onChange={(e) => setVariantId(e.target.value)} placeholder="variant_id"
          style={{ background: BRAND.card, color: "white", border: `1px solid ${BRAND.panel}`, borderRadius: "8px", padding: "0.55rem 0.7rem", fontSize: "0.82rem", fontFamily: "ui-monospace, monospace" }} />
      </div>
      <div>
        <button onClick={run} disabled={busy} style={{
          background: busy ? BRAND.panel : BRAND.orange, color: "white", border: "none",
          borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
          cursor: busy ? "default" : "pointer",
        }}>
          {busy ? "Generating (5–15s)…" : "🖨️ Generate mockup"}
        </button>
      </div>
      {result && !result.error && (
        <div style={{ background: "rgba(112,190,68,0.08)", border: `1px solid ${BRAND.green}`, borderRadius: "10px", padding: "0.8rem 1rem", color: "white" }}>
          <div style={{ fontSize: "0.84rem", marginBottom: "0.6rem" }}>✓ {result.mockups.length} mockup(s) generated:</div>
          <div style={{ display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
            {result.mockups.map((m, i) => (
              <a key={i} href={m.mockup_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <img src={m.mockup_url} alt={m.placement} style={{ maxWidth: "260px", maxHeight: "260px", borderRadius: "8px", border: `1px solid ${BRAND.panel}` }} />
                <div style={{ fontSize: "0.72rem", color: BRAND.dim, textAlign: "center", marginTop: "0.3rem" }}>{m.placement}</div>
              </a>
            ))}
          </div>
        </div>
      )}
      {result?.error && (
        <div style={{ background: "rgba(201,68,68,0.12)", border: "1px solid #c94444", borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ⚠ {result.error}
        </div>
      )}
    </section>
  );
}

// ── Printful connection test ────────────────────────────────────────────────
// One-tap call to GET /stores via the Printful API. Confirms token works
// and store exists before Phase 1 of the POD pipeline gets wired up.
function PrintfulConnectionTestSection() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    setBusy(true); setResult(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "testPrintfulConnection");
      const res = await fn({});
      setResult(res.data);
    } catch (err) {
      setResult({ error: err?.message || "Connection test failed" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🖨️ Test Printful connection</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Confirms the <code style={{ background: BRAND.card, padding: "0.05rem 0.25rem", borderRadius: "4px", fontSize: "0.78rem" }}>PRINTFUL_API_TOKEN</code> secret
        is valid and lists the stores in your Printful account. Run before
        wiring up POD checkout.
      </div>
      <div>
        <button
          onClick={run}
          disabled={busy}
          style={{
            background: busy ? BRAND.panel : BRAND.blue, color: "white", border: "none",
            borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
            cursor: busy ? "default" : "pointer",
          }}
        >
          {busy ? "Checking…" : "🖨️ Test connection"}
        </button>
      </div>
      {result && !result.error && (
        <div style={{ background: "rgba(112,190,68,0.08)", border: `1px solid ${BRAND.green}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <div>✓ Connected. {result.storeCount} store(s) on this account:</div>
          {result.stores.map((s) => (
            <div key={s.id} style={{ fontSize: "0.8rem", color: BRAND.cream, paddingLeft: "0.5rem" }}>
              • <strong>{s.name}</strong> <span style={{ opacity: 0.6 }}>(id: {s.id}, type: {s.type})</span>
            </div>
          ))}
          {result.storeCount === 0 && (
            <div style={{ color: BRAND.gold, fontSize: "0.8rem" }}>
              ⚠ Token works but no store found. Create one via Printful Dashboard → Stores → "Connect via API" before we can submit orders.
            </div>
          )}
        </div>
      )}
      {result?.error && (
        <div style={{ background: "rgba(201,68,68,0.12)", border: "1px solid #c94444", borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ⚠ {result.error}
        </div>
      )}
    </section>
  );
}

// ── Bluesky connection test ─────────────────────────────────────────────
// Logs into the @thejoyofpancakes.com Bluesky account with the app
// password, posts a hello-world, and immediately deletes it. Proves the
// handle + BLUESKY_APP_PASSWORD secret + posting plumbing end-to-end
// without anything test-flavored staying public.
function BlueskyConnectionTestSection() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    setBusy(true); setResult(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "testBlueskyConnection");
      const res = await fn({});
      setResult(res.data);
    } catch (err) {
      setResult({ error: err?.message || "Connection test failed" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🦋 Test Bluesky connection</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Logs into Bluesky with the <code style={{ background: BRAND.card, padding: "0.05rem 0.25rem", borderRadius: "4px", fontSize: "0.78rem" }}>BLUESKY_APP_PASSWORD</code> secret,
        posts a throwaway test post, and deletes it. Run once after setting
        the secret, and again any time posting starts failing.
      </div>
      <div>
        <button
          onClick={run}
          disabled={busy}
          style={{
            background: busy ? BRAND.panel : BRAND.blue, color: "white", border: "none",
            borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
            cursor: busy ? "default" : "pointer",
          }}
        >
          {busy ? "Checking…" : "🦋 Test connection"}
        </button>
      </div>
      {result && !result.error && (
        <div style={{ background: "rgba(112,190,68,0.08)", border: `1px solid ${BRAND.green}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <div>✓ Connected as <strong>@{result.handle}</strong> — posted + deleted a test post in {result.ms}ms.</div>
          {result.did && (
            <div style={{ fontSize: "0.78rem", color: BRAND.cream, opacity: 0.7 }}>{result.did}</div>
          )}
        </div>
      )}
      {result?.error && (
        <div style={{ background: "rgba(201,68,68,0.12)", border: "1px solid #c94444", borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ⚠ {result.error}
        </div>
      )}
    </section>
  );
}

// ── Cutout A/B comparison ────────────────────────────────────────────────
// Runs a set of order IDs through three background-removal services
// (BiRefNet/Replicate, Photoroom, remove.bg) and uploads a side-by-side
// HTML page so we can pick which one to swap in for the default cutout
// pipeline. Default IDs prefilled with the 8 known-problematic pancakes
// — paste different IDs to compare different cases.
function CutoutAbComparisonSection() {
  const DEFAULT_IDS = [
    "rNmr58gejZaYTTjcpCZ",
    "4GTXRfBpODdUMeETflgj",
    "9dl4PHKvqkC9dQIKMouK",
    "TSPEyXUNz110cWX0vmV6",
    "8eadkwJ4b1QrHqvx1Dau",
    "5XgTsIckLGatmN8Be7o9",
    "KGvnOXpmoGMQV89hi30T",
    "yhpStmPRx8CieS3wTfI6",
  ].join("\n");

  const [idsText, setIdsText] = useState(DEFAULT_IDS);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Per-service toggles — skip paid services on Replicate-only rerun
  const [useReplicate, setUseReplicate] = useState(true);
  const [usePhotoroom, setUsePhotoroom] = useState(true);
  const [useRemoveBg,  setUseRemoveBg]  = useState(true);

  async function run() {
    const ids = idsText.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    if (ids.length === 0) { setResult({ error: "Need at least one order ID." }); return; }
    if (!useReplicate && !usePhotoroom && !useRemoveBg) { setResult({ error: "Pick at least one service." }); return; }
    const costPer = (useReplicate ? 0.005 : 0) + (usePhotoroom ? 0.15 : 0) + (useRemoveBg ? 0.22 : 0);
    if (!window.confirm(`Run ${ids.length} pancakes through ${[useReplicate&&"InSPyReNet",usePhotoroom&&"Photoroom",useRemoveBg&&"remove.bg"].filter(Boolean).join(" + ")}? Spends ~$${(ids.length * costPer).toFixed(2)} in API credits.`)) return;
    setBusy(true); setResult(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "abCutoutComparison", { timeout: 540000 });
      const res = await fn({
        orderIds: ids,
        services: { replicate: useReplicate, photoroom: usePhotoroom, removeBg: useRemoveBg },
      });
      setResult(res.data);
    } catch (err) {
      setResult({ error: err?.message || "Comparison failed" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🎨 Cutout A/B comparison</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Runs the listed pancakes through three background-removal services and uploads a
        side-by-side HTML page on a transparency-checker background. Spends real API
        credits (~$0.38/pancake total across services). Staff-only.
      </div>
      <textarea
        value={idsText}
        onChange={(e) => setIdsText(e.target.value)}
        placeholder="order IDs, one per line or comma-separated"
        rows={6}
        style={{
          background: BRAND.card, color: "white", border: `1px solid ${BRAND.panel}`,
          borderRadius: "8px", padding: "0.55rem 0.7rem", fontSize: "0.82rem",
          fontFamily: "ui-monospace, monospace", resize: "vertical",
        }}
      />
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", fontSize: "0.82rem", color: BRAND.cream }}>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
          <input type="checkbox" checked={useReplicate} onChange={e => setUseReplicate(e.target.checked)} />
          InSPyReNet (Replicate) <span style={{ color: BRAND.dim }}>~$0.005</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
          <input type="checkbox" checked={usePhotoroom} onChange={e => setUsePhotoroom(e.target.checked)} />
          Photoroom <span style={{ color: BRAND.dim }}>~$0.15</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
          <input type="checkbox" checked={useRemoveBg} onChange={e => setUseRemoveBg(e.target.checked)} />
          remove.bg <span style={{ color: BRAND.dim }}>~$0.22</span>
        </label>
      </div>
      <div>
        <button
          onClick={run}
          disabled={busy}
          style={{
            background: busy ? BRAND.panel : BRAND.orange, color: "white", border: "none",
            borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
            cursor: busy ? "default" : "pointer",
          }}
        >
          {busy ? "Running (up to 9 min)…" : "🎨 Run comparison"}
        </button>
      </div>
      {result && !result.error && (
        <div style={{ background: "rgba(112,190,68,0.08)", border: `1px solid ${BRAND.green}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <div>✓ {result.counts.succeeded} of {result.counts.total} succeeded ({result.counts.failed} failed).</div>
          <a href={result.htmlUrl} target="_blank" rel="noopener noreferrer"
             style={{ color: BRAND.gold, fontWeight: 700, textDecoration: "underline", wordBreak: "break-all" }}>
            Open comparison page →
          </a>
          {result.counts.failed > 0 && (
            <details style={{ marginTop: "0.4rem" }}>
              <summary style={{ cursor: "pointer", color: BRAND.dim, fontSize: "0.78rem" }}>See failures</summary>
              <div style={{ marginTop: "0.4rem", fontSize: "0.78rem", color: BRAND.cream }}>
                {result.results.filter(r => r.error).map(r => (
                  <div key={r.orderId}>• {r.orderId}: {r.error}</div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
      {result?.error && (
        <div style={{ background: "rgba(201,68,68,0.12)", border: "1px solid #c94444", borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ⚠ {result.error}
        </div>
      )}
    </section>
  );
}

// ── Reconcile tasks ─────────────────────────────────────────────────────
// One-shot: re-evaluates every booking against the task registry and
// creates/deletes tasks to match. Fixes bulk-import artifacts.
// ── Backfill design sequences ────────────────────────────────────────────
// One-shot: renumbers every flash/freestyle order chronologically within its
// design group so "Saturnina (#6)" etc. is consistent across orders placed
// before the sequence-stamping deploy. Idempotent — safe to re-run; produces
// the same numbering each time since it sorts by submittedAt.
function BackfillDesignSequencesSection() {
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);

  async function doBackfill() {
    if (!window.confirm("Renumber all flash/freestyle orders chronologically within each design group? This overwrites any existing sequence numbers, including recent ones.")) return;
    setBusy(true); setResult(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "backfillDesignSequences", { timeout: 540000 });
      const res = await fn({});
      setResult(res.data);
    } catch (err) {
      setResult({ error: err?.message || "Backfill failed" });
    } finally {
      setBusy(false);
    }
  }

  // Sort design groups by count descending so the most-drawn designs lead.
  const rows = result?.counts
    ? Object.entries(result.counts).sort((a, b) => b[1] - a[1])
    : null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🔢 Fix pancake titles &amp; numbering</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Repairs pancakes showing as <strong style={{ color: BRAND.cream }}>"Untitled"</strong>: every
        flash and freestyle pancake gets its lifetime number back ("Saturnina (#6)",
        "Freestyle (#7)"), and custom pancakes the guest never titled get numbered too
        ("Untitled #45") — all renumbered in the order they were drawn. Those numbers ARE
        the titles on the library, overlays, keepsakes, and public gallery. Also repairs
        renamed characters (e.g. Robert → Trav) and re-publishes the public pages when done.
        Safe to re-run — same result every time. Customs with a guest-written title keep it
        (rename any in the Library).
      </div>
      <div>
        <button
          onClick={doBackfill}
          disabled={busy}
          style={{
            background: busy ? BRAND.panel : BRAND.orange, color: "white", border: "none",
            borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
            cursor: busy ? "default" : "pointer",
          }}
        >
          {busy ? "Renumbering…" : "🔢 Renumber all flash/freestyle"}
        </button>
      </div>
      {rows && (
        <div style={{ background: "rgba(112,190,68,0.08)", border: `1px solid ${BRAND.green}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
          <div>
            ✓ Renumbered {result.totalWrites} orders across {rows.length} design{rows.length !== 1 ? "s" : ""}.
            {result.labelRepairs > 0 && ` Repaired ${result.labelRepairs} stale label${result.labelRepairs !== 1 ? "s" : ""}.`}
            {result.skipped > 0 && ` Skipped ${result.skipped} malformed.`}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem 1rem", marginTop: "0.25rem" }}>
            {rows.map(([key, count]) => (
              <span key={key} style={{ color: BRAND.cream, fontSize: "0.82rem" }}>
                <span style={{ color: BRAND.gold, fontWeight: 700, textTransform: "capitalize" }}>{key}</span>: {count}
              </span>
            ))}
          </div>
        </div>
      )}
      {result?.error && (
        <div style={{ background: "rgba(201,68,68,0.12)", border: "1px solid #c94444", borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ⚠ {result.error}
        </div>
      )}
    </section>
  );
}

// ── Backfill historical shift IDs ─────────────────────────────────────────
// Repairs content/historical pancakes whose `shiftId` got assigned to a real
// event shift at creation (Richard Pryor → Tower Grove etc). Two modes:
//   - Auto: any order with `_isHistorical: true` gets pinned to the
//     "historical_archive" container.
//   - Force-IDs: paste a newline/comma separated list of order IDs. Each is
//     force-flagged `_isHistorical: true` + reassigned. Use this for content
//     pancakes that were never marked historical.
function BackfillHistoricalShiftsSection() {
  const [busy, setBusy] = useState(false);
  const [forceIdsText, setForceIdsText] = useState("");
  const [result, setResult] = useState(null);

  async function doBackfill() {
    if (!window.confirm("Reassign all historical/content pancakes to the Historical Archive shift? Idempotent — safe to re-run.")) return;
    setBusy(true); setResult(null);
    try {
      const forceIds = forceIdsText
        .split(/[\s,]+/)
        .map(s => s.trim())
        .filter(Boolean);
      const fn = httpsCallable(getFunctions(getApp()), "backfillHistoricalShiftIds", { timeout: 120000 });
      const res = await fn({ forceIds });
      setResult(res.data);
    } catch (err) {
      setResult({ error: err?.message || "Backfill failed" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>📦 Backfill historical shift IDs</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Pins every <code style={{ background: BRAND.card, padding: "0.05rem 0.25rem", borderRadius: "4px", fontSize: "0.78rem" }}>_isHistorical: true</code> order to the
        Historical Archive shift, so content pancakes stop showing up under
        random market tabs on the outro. Optionally paste order IDs below to
        force-flag pancakes that were never marked historical at creation.
      </div>
      <textarea
        value={forceIdsText}
        onChange={(e) => setForceIdsText(e.target.value)}
        placeholder="Optional: order IDs to force-flag (one per line or comma-separated)"
        rows={3}
        style={{
          background: BRAND.card, color: "white", border: `1px solid ${BRAND.panel}`,
          borderRadius: "8px", padding: "0.55rem 0.7rem", fontSize: "0.82rem",
          fontFamily: "ui-monospace, monospace", resize: "vertical",
        }}
      />
      <div>
        <button
          onClick={doBackfill}
          disabled={busy}
          style={{
            background: busy ? BRAND.panel : BRAND.orange, color: "white", border: "none",
            borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
            cursor: busy ? "default" : "pointer",
          }}
        >
          {busy ? "Reassigning…" : "📦 Reassign historical pancakes"}
        </button>
      </div>
      {result && !result.error && (
        <div style={{ background: "rgba(112,190,68,0.08)", border: `1px solid ${BRAND.green}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
          <div>
            ✓ Auto-fixed {result.autoFixed} of {result.totalScanned} historical orders.
            {result.forceFixed > 0 && ` Force-flagged ${result.forceFixed} by ID.`}
          </div>
          {(result.autoSamples?.length > 0 || result.forceSamples?.length > 0) && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem", marginTop: "0.25rem", color: BRAND.cream, fontSize: "0.78rem" }}>
              {result.autoSamples?.map(s => (
                <div key={`a-${s.id}`}>• {s.name} <span style={{ opacity: 0.55 }}>({s.id.slice(0, 8)}…)</span></div>
              ))}
              {result.forceSamples?.map(s => (
                <div key={`f-${s.id}`}>• {s.name} <span style={{ opacity: 0.55 }}>({s.id.slice(0, 8)}…) — forced</span></div>
              ))}
            </div>
          )}
        </div>
      )}
      {result?.error && (
        <div style={{ background: "rgba(201,68,68,0.12)", border: "1px solid #c94444", borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ⚠ {result.error}
        </div>
      )}
    </section>
  );
}

// ── Backfill AI descriptions ──────────────────────────────────────────────
// Client-side iteration across the gallery. Concurrency 4. Confirm modal
// surfaces count + estimated cost + time before spending real API dollars.
function BackfillAiDescriptionsSection() {
  const [busy,         setBusy]         = useState(false);
  const [mode,         setMode]          = useState(null);  // "all" | "missing"
  const [progress,     setProgress]     = useState(null);  // { done, total, errors }
  const [result,       setResult]       = useState(null);
  const [confirmState, setConfirmState] = useState(null);  // null | { scope, candidates }
  const cancelRef = useRef({ cancelled: false });

  async function prepareBackfill(scope) {
    setBusy(true);
    setResult(null);
    try {
      const snap = await getDocs(query(collection(db, "orders"), where("state", "==", "done")));
      const candidates = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(o => o.photo_url && !o.trashedAt)
        .filter(o => scope === "all" || !o.ai_description);

      if (candidates.length === 0) {
        setResult({ kind: "empty" });
        setBusy(false);
        return;
      }
      setConfirmState({ scope, candidates });
      setBusy(false);
    } catch (err) {
      setResult({ kind: "error", msg: err?.message || "Gallery scan failed" });
      setBusy(false);
    }
  }

  async function runBackfill() {
    const { candidates, scope } = confirmState;
    setConfirmState(null);
    setMode(scope);
    setBusy(true);
    cancelRef.current = { cancelled: false };
    setProgress({ done: 0, total: candidates.length, errors: 0 });

    const fn = httpsCallable(getFunctions(getApp()), "generatePancakeDescription");
    let done = 0, errors = 0;
    const CONCURRENCY = 4;
    const queue = [...candidates];

    async function worker() {
      while (queue.length > 0 && !cancelRef.current.cancelled) {
        const o = queue.shift();
        try {
          await fn({
            orderId:        o.id,
            photoUrl:       o.photo_url,
            tokenId:        o.tokenId || o.id,
            subjectName:    o.subjectName || null,
            recipientName:  o.recipientName || null,
            notes:          o.notes || null,
            orderType:      o.orderType || null,
            designLabel:    o.designLabel || null,
            favoriteColors: o.favoriteColors || null,
            isFreestyle:    o.orderType === "freestyle_flash" || null,
            artistName:     o.artistName || null,
            drawingTimeMs:  o.drawingTimeMs || null,
            cookTimeMs:     o.cookTimeMs || null,
            skipCutout:     true,
          });
        } catch (err) {
          errors++;
          console.warn("Backfill failed for", o.id, err);
        }
        done++;
        setProgress({ done, total: candidates.length, errors });
      }
    }

    await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));

    setResult({
      kind:      "done",
      processed: done,
      errors,
      cancelled: cancelRef.current.cancelled,
    });
    setBusy(false);
  }

  const estCost = confirmState ? (confirmState.candidates.length * 0.011).toFixed(2) : null;
  const estMins = confirmState ? Math.max(1, Math.ceil(confirmState.candidates.length * 25 / 60 / 4)) : null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>✨ Backfill AI descriptions</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Re-runs the Sonnet alt-text generator across the gallery. Each call is
        ~20–30s at concurrency 4. Keep this tab open while it runs — progress
        is client-side. Sonnet is ~$0.011 per pancake. <strong style={{ color: BRAND.cream }}>This gets expensive with a big gallery — confirm the count before running.</strong>
      </div>
      <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
        <button
          onClick={() => prepareBackfill("missing")}
          disabled={busy}
          style={{
            background: busy ? BRAND.panel : BRAND.sky, color: "white", border: "none",
            borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
            cursor: busy ? "default" : "pointer",
            opacity: busy && mode !== "missing" ? 0.5 : 1,
          }}
        >
          {busy && mode === "missing" ? "Processing…" : "Only pancakes missing a description"}
        </button>
        <button
          onClick={() => prepareBackfill("all")}
          disabled={busy}
          style={{
            background: busy ? BRAND.panel : BRAND.orange, color: "white", border: "none",
            borderRadius: "10px", padding: "0.7rem 1rem", fontSize: "0.9rem", fontWeight: 700,
            cursor: busy ? "default" : "pointer",
            opacity: busy && mode !== "all" ? 0.5 : 1,
          }}
        >
          {busy && mode === "all" ? "Processing…" : "Re-run everything"}
        </button>
        {busy && progress && (
          <button
            onClick={() => { cancelRef.current.cancelled = true; }}
            style={{ ...miniBtn, padding: "0.6rem 0.9rem", fontSize: "0.82rem", color: "#ef537e", borderColor: "#ef537e" }}
          >
            Cancel
          </button>
        )}
      </div>
      {progress && (
        <div style={{ background: "rgba(74,164,225,0.08)", border: `1px solid ${BRAND.sky}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          Processing {progress.done} / {progress.total}
          {progress.errors > 0 && ` · ${progress.errors} error${progress.errors !== 1 ? "s" : ""}`}
          <div style={{ height: "4px", background: "rgba(255,255,255,0.1)", borderRadius: "2px", marginTop: "0.4rem", overflow: "hidden" }}>
            <div style={{ width: `${(progress.done / progress.total) * 100}%`, height: "100%", background: BRAND.sky, transition: "width 0.2s" }} />
          </div>
        </div>
      )}
      {result?.kind === "done" && (
        <div style={{ background: "rgba(112,190,68,0.08)", border: `1px solid ${BRAND.green}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ✓ {result.cancelled ? "Cancelled mid-run. " : ""}Reprocessed {result.processed} pancake{result.processed !== 1 ? "s" : ""}
          {result.errors > 0 && `, ${result.errors} failed`}.
        </div>
      )}
      {result?.kind === "empty" && (
        <div style={{ background: "rgba(74,164,225,0.08)", border: `1px solid ${BRAND.sky}`, borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          Nothing to do — no pancakes match that scope.
        </div>
      )}
      {result?.kind === "error" && (
        <div style={{ background: "rgba(201,68,68,0.12)", border: "1px solid #c94444", borderRadius: "10px", padding: "0.8rem 1rem", fontSize: "0.84rem", color: "white" }}>
          ⚠ {result.msg}
        </div>
      )}

      {/* Confirm modal */}
      {confirmState && (
        <div
          onClick={() => setConfirmState(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000, padding: "1.5rem",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: BRAND.card, border: `1px solid ${BRAND.border}`,
              borderRadius: "14px", padding: "1.5rem", maxWidth: "28rem",
              width: "100%", display: "flex", flexDirection: "column", gap: "1rem",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
          >
            <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "1.05rem" }}>
              Re-run AI descriptions?
            </div>
            <div style={{ color: BRAND.cream, fontSize: "0.9rem", lineHeight: 1.55 }}>
              This will call Claude Sonnet on{" "}
              <strong style={{ color: BRAND.orange }}>{confirmState.candidates.length}</strong>{" "}
              pancake{confirmState.candidates.length !== 1 ? "s" : ""}{" "}
              {confirmState.scope === "missing" ? "missing a description" : "in your gallery"}.
            </div>
            <div style={{ background: BRAND.panel, borderRadius: "8px", padding: "0.85rem 1rem", fontSize: "0.84rem", color: BRAND.muted, lineHeight: 1.9, border: `1px solid ${BRAND.border}` }}>
              <div>💸 Estimated cost: <strong style={{ color: BRAND.cream }}>~${estCost}</strong> <span style={{ color: BRAND.dim, fontSize: "0.78rem" }}>(Sonnet, ~$0.011/call)</span></div>
              <div>⏱ Estimated runtime: <strong style={{ color: BRAND.cream }}>~{estMins} min</strong> <span style={{ color: BRAND.dim, fontSize: "0.78rem" }}>(4 parallel)</span></div>
              <div style={{ color: "#ef537e" }}>⚠️ Keep this tab open — you can cancel partway</div>
            </div>
            <div style={{ display: "flex", gap: "0.6rem", justifyContent: "flex-end" }}>
              <button
                onClick={() => setConfirmState(null)}
                style={{
                  background: BRAND.panel, color: BRAND.cream, border: `1px solid ${BRAND.border}`,
                  borderRadius: "8px", padding: "0.65rem 1.1rem", fontSize: "0.88rem",
                  fontWeight: 600, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={runBackfill}
                style={{
                  background: BRAND.orange, color: "white", border: "none",
                  borderRadius: "8px", padding: "0.65rem 1.3rem", fontSize: "0.88rem",
                  fontWeight: 700, cursor: "pointer",
                }}
              >
                Yes, run it
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Refresh public data ──────────────────────────────────────────────────
// Rebuilds every public summary doc (hero drift sample, /m meditations feed,
// /events, /gallery) on demand. Lives in Maintenance → "Public site" — it was
// previously buried inside the calendar-import card on the Data tab, where
// nobody could find it.
function RefreshPublicDataSection() {
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);

  async function doRefreshPublicDocs() {
    setBusy(true); setResult(null);
    try {
      const fn1 = httpsCallable(getFunctions(getApp()), "triggerPublicPancakeSampleSync");
      const fn2 = httpsCallable(getFunctions(getApp()), "triggerPublicMeditationsSync");
      const fn3 = httpsCallable(getFunctions(getApp()), "syncPublicEventsNow");
      const fn4 = httpsCallable(getFunctions(getApp()), "triggerPublicGallerySync");
      const [r1, r2, r3, r4] = await Promise.all([fn1({}), fn2({}), fn3({}), fn4({})]);
      setResult({ refresh: true, pancakes: r1.data?.itemCount, meditations: r2.data?.itemCount, events: r3.data?.itemCount, gallery: r4.data?.itemCount });
    } catch (err) { setResult({ error: err?.message || "Refresh failed" }); }
    finally { setBusy(false); }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🔄 Refresh what the website shows</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Rebuilds every public summary the website reads: the homepage pancake drift, the
        meditations feed (/m), the events page, and the Pancakeopedia gallery. Use it whenever
        the public site looks stale — after editing photos, titles, meditations, or events.
        Safe to run anytime; it only re-publishes what's already there.
      </div>
      <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={doRefreshPublicDocs} disabled={busy} style={{
          background: busy ? BRAND.panel : BRAND.green, color: "white", border: "none",
          borderRadius: "10px", padding: "0.65rem 1rem", fontSize: "0.85rem", fontWeight: 700,
          cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
        }}>
          {busy ? "Refreshing…" : "🔄 Rebuild all public pages"}
        </button>
        {result?.refresh && (
          <span style={{ color: BRAND.green, fontSize: "0.82rem", fontWeight: 700 }}>
            ✓ {result.pancakes} pancakes · {result.meditations} meditations · {result.events} events
          </span>
        )}
        {result?.error && <span style={{ color: BRAND.pink, fontSize: "0.82rem", fontWeight: 700 }}>⚠ {result.error}</span>}
      </div>
    </section>
  );
}

// ── Calendar import button ───────────────────────────────────────────────
// Calls importCalendarEvents to pull public events from Google Calendar
// into the CRM as bookings. Deduplicates by calendar event ID.
function CalendarImportSection() {
  const [busy, setBusy]       = useState(false);
  const [result, setResult]   = useState(null);

  async function doImport() {
    setBusy(true); setResult(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "importCalendarEvents");
      const res = await fn({});
      setResult(res.data);
    } catch (err) {
      setResult({ error: err?.message || "Import failed" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginTop: "0.5rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>📅 Import from Google Calendar</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Reads upcoming + recent events from the public Google Calendar and creates CRM
        bookings with <code style={{ background: BRAND.card, padding: "0.05rem 0.25rem", borderRadius: "4px", fontSize: "0.78rem" }}>isPublicEvent: true</code>.
        Skips events already imported (deduped by calendar event ID). The{" "}
        <code style={{ background: BRAND.card, padding: "0.05rem 0.25rem", borderRadius: "4px", fontSize: "0.78rem" }}>/events</code>{" "}
        page auto-refreshes within seconds.
      </div>
      <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap", alignItems: "center" }}>
        <button
          onClick={doImport}
          disabled={busy}
          style={{
            background: busy ? BRAND.panel : "#1d52a0", color: "white", border: "none",
            borderRadius: "10px", padding: "0.65rem 1rem", fontSize: "0.85rem", fontWeight: 700,
            cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? "Working…" : "📅 Import calendar events"}
        </button>
        <button
          onClick={async () => {
            setBusy(true); setResult(null);
            try {
              const fn = httpsCallable(getFunctions(getApp()), "backfillSeriesIds");
              const res = await fn({});
              setResult({ backfill: true, ...res.data });
            } catch (err) { setResult({ error: err?.message || "Backfill failed" }); }
            finally { setBusy(false); }
          }}
          disabled={busy}
          style={{
            background: busy ? BRAND.panel : BRAND.card, color: BRAND.sky, border: `1px solid ${BRAND.sky}`,
            borderRadius: "10px", padding: "0.65rem 1rem", fontSize: "0.85rem", fontWeight: 700,
            cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? "Working…" : "🔗 Backfill series + clean descriptions"}
        </button>
      </div>
      <div>
        {result && !result.error && !result.backfill && (
          <span style={{ color: BRAND.green, fontSize: "0.82rem", fontWeight: 700 }}>
            ✓ {result.imported} imported, {result.skipped} skipped ({result.total} total in calendar)
          </span>
        )}
        {result && !result.error && result.backfill && (
          <span style={{ color: BRAND.green, fontSize: "0.82rem", fontWeight: 700 }}>
            ✓ {result.patched} bookings patched (series IDs + description cleanup)
          </span>
        )}
        {result?.error && (
          <span style={{ color: BRAND.pink, fontSize: "0.82rem", fontWeight: 700 }}>
            ⚠ {result.error}
          </span>
        )}
      </div>
    </section>
  );
}

// Reads all shift_summaries and surfaces suspicious entries — wrong avg draw
// times, phantom orders, container shifts leaking in, test data ghosts, etc.
// Also offers per-doc deletion so Dan can surgically remove corrupted entries.
function ShiftSummaryAudit() {
  const [rows, setRows]         = useState(null); // null = not run
  const [deleting, setDeleting] = useState(null);

  async function runAudit() {
    const snap = await getDocs(query(collection(db, "shift_summaries"), orderBy("closedAt", "desc"), limit(200)));
    const all = snap.docs.map(d => {
      const s = d.data();
      // Compute weighted avg draw from artistBreakdown
      const entries = Object.values(s.artistBreakdown ?? {}).filter(a => a.avgDrawMs != null && (a.totalOrders ?? a.count ?? 0) > 0);
      const totalN  = entries.reduce((n, a) => n + (a.totalOrders ?? a.count ?? 1), 0);
      const avgDraw = entries.length
        ? Math.round(entries.reduce((n, a) => n + a.avgDrawMs * (a.totalOrders ?? a.count ?? 1), 0) / totalN)
        : null;

      const isContainer = !!(s._isHistoricalContainer || s._isOrphanContainer
        || d.id === "historical_archive" || d.id === "orphaned_orders");

      const flags = [];
      if (s._isTestData) flags.push("TEST");
      if (isContainer) flags.push("CONTAINER");
      // Only flag empty/timing issues on real shifts, not containers
      if (!isContainer) {
        if ((s.totalOrders ?? 0) === 0) flags.push("EMPTY");
        if (avgDraw && avgDraw > 600000) flags.push("AVG_DRAW_>10m");
        else if (avgDraw && avgDraw > 60000) flags.push("AVG_DRAW_>1m");
        if ((s.durationMs ?? 0) > 86400000) flags.push("DURATION_>24h");
      }

      return {
        id: d.id,
        eventName:   s.eventName || "(unnamed)",
        closedAt:    s.closedAt?.toDate?.()?.toISOString?.()?.slice(0, 16) ?? "(no date)",
        totalOrders: s.totalOrders ?? 0,
        flashCount:  s.flashCount ?? 0,
        customCount: s.customCount ?? 0,
        avgDrawMs:   avgDraw,
        durationMs:  s.durationMs ?? null,
        flags,
        raw: s,
      };
    });
    setRows(all);
  }

  async function deleteSummary(id) {
    if (!window.confirm(`Delete shift_summaries/${id}? This removes it from Insights permanently.`)) return;
    setDeleting(id);
    try {
      await deleteDoc(doc(db, "shift_summaries", id));
      setRows(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert("Delete failed: " + (err?.message || err));
    } finally {
      setDeleting(null);
    }
  }

  function fmtMs(ms) {
    if (ms == null) return "—";
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginTop: "0.5rem" }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.92rem" }}>🔍 Audit Insights Data</div>
      <div style={{ color: BRAND.dim, fontSize: "0.8rem", lineHeight: 1.5 }}>
        Reads all <code style={{ background: BRAND.card, padding: "0.05rem 0.25rem", borderRadius: "4px", fontSize: "0.78rem" }}>shift_summaries</code> and
        flags suspicious entries (container shifts, test data, absurd draw times, empty shifts).
        You can delete individual docs to fix corrupted Insights data.
      </div>
      <button
        onClick={runAudit}
        disabled={rows !== null}
        style={{
          background: rows ? BRAND.panel : BRAND.sky, color: "white", border: "none",
          borderRadius: "10px", padding: "0.6rem 1rem", fontSize: "0.85rem", fontWeight: 700,
          cursor: rows ? "default" : "pointer", opacity: rows ? 0.6 : 1, alignSelf: "flex-start",
        }}
      >
        {rows ? `Showing ${rows.length} summaries` : "🔍 Run audit"}
      </button>

      {rows && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.35rem" }}>
          {rows.map(r => {
            const isSuspect = r.flags.length > 0;
            return (
              <div key={r.id} style={{
                background: isSuspect ? "rgba(239,83,126,0.08)" : BRAND.card,
                border: `1px solid ${isSuspect ? "rgba(239,83,126,0.35)" : BRAND.border}`,
                borderRadius: "8px", padding: "0.6rem 0.8rem", fontSize: "0.78rem",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", marginBottom: "0.35rem" }}>
                  <div style={{ fontWeight: 700, color: "white", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {r.eventName}
                  </div>
                  <div style={{ color: BRAND.dim, flexShrink: 0, fontSize: "0.72rem" }}>{r.closedAt}</div>
                </div>
                <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", color: BRAND.muted, fontSize: "0.75rem" }}>
                  <span>Orders: <b style={{ color: "white" }}>{r.totalOrders}</b></span>
                  <span>Flash: <b style={{ color: BRAND.gold }}>{r.flashCount}</b></span>
                  <span>Custom: <b style={{ color: BRAND.sky }}>{r.customCount}</b></span>
                  <span>Avg draw: <b style={{ color: r.avgDrawMs && r.avgDrawMs > 600000 ? BRAND.pink : "white" }}>{fmtMs(r.avgDrawMs)}</b></span>
                  <span>Duration: <b>{r.durationMs ? `${Math.round(r.durationMs / 60000)}m` : "—"}</b></span>
                </div>
                <div style={{ marginTop: "0.35rem", display: "flex", gap: "0.35rem", flexWrap: "wrap", alignItems: "center" }}>
                  {r.flags.map(f => (
                    <span key={f} style={{
                      background: f === "CONTAINER" ? "rgba(74,164,225,0.15)" : "rgba(239,83,126,0.15)",
                      color: f === "CONTAINER" ? BRAND.sky : BRAND.pink,
                      borderRadius: "4px",
                      padding: "0.1rem 0.4rem", fontSize: "0.68rem", fontWeight: 700,
                    }}>{f}</span>
                  ))}
                  <button
                    onClick={() => deleteSummary(r.id)}
                    disabled={deleting === r.id}
                    style={{
                      marginLeft: "auto", background: "rgba(239,83,126,0.08)", border: `1px solid rgba(239,83,126,0.4)`,
                      color: BRAND.pink, borderRadius: "6px", padding: "0.25rem 0.6rem",
                      fontSize: "0.72rem", fontWeight: 700, cursor: "pointer",
                    }}
                  >
                    {deleting === r.id ? "Deleting…" : "🗑 Delete"}
                  </button>
                </div>
                <div style={{ marginTop: "0.3rem", color: BRAND.dim, fontSize: "0.68rem", fontFamily: "monospace", wordBreak: "break-all" }}>
                  id: {r.id}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

const miniBtn = {
  background: "none", border: `1px solid ${BRAND.border}`, borderRadius: "5px",
  color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", padding: "0.15rem 0.4rem",
  cursor: "pointer", lineHeight: 1,
};

const hudBtn = {
  background: "transparent", border: `1px solid rgba(74,164,225,0.28)`,
  color: "rgba(255,255,255,0.6)", borderRadius: "8px",
  padding: "0.4rem 0.75rem", fontSize: "0.8rem", cursor: "pointer",
};
